import { createEffect, createMemo, createSignal, For, onCleanup, Show, type JSX } from "solid-js"
import { Button } from "@openanalyst-ai/ui/button"
import { Icon } from "@openanalyst-ai/ui/icon"
import { Tooltip } from "@openanalyst-ai/ui/tooltip"
import { useDialog } from "@openanalyst-ai/ui/context/dialog"

// A generation fails with 402 INSUFFICIENT_CREDITS — the backend message carries the
// shortfall. Recognize it so the Preview can show a real "Add credits" CTA instead of
// a raw error (mirrors the composer's rate-limit banner). No auto-retry (handled upstream).
const isInsufficientCredits = (error: unknown) => /insufficient credit|\b402\b/i.test(String(error ?? ""))
import { VisualPipelineBlockView, type VisualPipelineBlock, type VisualPipelineGraph } from "./visual-pipeline-block"

type GenerativeInput = {
  key: string
  label: string
  type: "text" | "textarea" | "select" | "number" | "range" | "toggle"
  default?: string | number | boolean
  required?: boolean
  options?: string[]
  min?: number
  max?: number
  step?: number
}

type AssetKindHint = "image" | "audio" | "video" | "visual"

type GenerativeModel = {
  id: string
  name: string
  studio: string
  mode: string
  accepts?: string[]
  outputs?: string[]
  maxReferenceAssets?: number
  requiredAssets?: AssetKindHint[]
  inputs?: GenerativeInput[]
}

type RecipeScenario = {
  id: string
  title: string
  prompt?: string
}

type GenerativeRecipe = {
  id: string
  title: string
  description?: string
  source: string
  sourceLabel?: string
  sourceRef?: Record<string, unknown>
  requiredAssets?: string[]
  maxReferenceAssets?: number
  minVariants?: number
  maxVariants?: number
  defaultVariants?: number
  aspectRatios?: string[]
  defaultAspectRatio?: string
  scenarios?: RecipeScenario[]
  slots?: Array<{ id: string; label: string; kind: string; required?: boolean; unsupportedReason?: string }>
  template?: {
    id: string
    title: string
    description?: string
    fields?: Array<{
      key: string
      label: string
      type: "asset" | "integer" | "select" | "text" | "textarea" | "url"
      required?: boolean
      default?: string | number
      minimum?: number
      maximum?: number
      options?: string[]
      source?: "asset" | "params" | "prompt"
    }>
    outputs?: Array<"image" | "video" | "text">
  }
  disabledReason?: string
}

type RecipeRun = {
  id: string
  recipeID: string
  source?: string
  title: string
  scenarioID?: string
  prompt?: string
  params?: Record<string, unknown>
  assetIDs?: string[]
  jobIDs?: string[]
  status: string
  error?: string
  jobs?: Array<Record<string, unknown>>
  createdAt?: number
  updatedAt?: number
  completedAt?: number
}

type PipelineDesign = {
  id: string
  title: string
  graph: VisualPipelineGraph
  graphHash?: string
  validation?: VisualPipelineBlock["validation"]
  lastExportedGraphHash?: string
  lastExportedRecipeId?: string
  staleExport?: boolean
}

type GenerativePipelines = {
  nodeTypes: VisualPipelineBlock["nodeTypes"]
  designs: PipelineDesign[]
  commands: Record<string, string>
}

type BrandDNAVersion = {
  id: string
  versionNo?: number
  source?: string
  designPath?: string
  designHash?: string
  warnings?: string[]
  createdAt?: number
}

type BrandDNAProfile = {
  id: string
  name: string
  sourceUrl?: string
  status?: string
  designPath?: string | null
  syncStatus?: string
  syncWarnings?: string[]
  versionNo?: number
  lastSyncedAt?: number
  versions?: BrandDNAVersion[]
  colors?: string[]
  fonts?: string[]
}

type BrandDNAState = {
  activeID?: string | null
  profiles?: BrandDNAProfile[]
  commands?: Record<string, string>
}

type CampaignPlan = {
  id: string
  title: string
  goal?: string
  platforms?: string[]
  language?: string
  status?: string
  exportPath?: string
}

type CampaignState = {
  plans?: CampaignPlan[]
  commands?: Record<string, string>
}

type CommandSubmit = (
  command: string,
  payload: Record<string, unknown>,
  extra?: Record<string, unknown>,
) => void | Promise<void>

export type GenerativeBlock = {
  id: string
  type: "generative"
  title?: string
  submitToolName: string
  uploadToolName: string
  uploadCommand: string
  policy: {
    enabled: boolean
    acknowledged: boolean
    disabledReason?: string | null
  }
  provider: {
    connected: boolean
  }
  catalog: {
    version: string
    source: string
    attribution: string
  }
  studios: Array<{
    id: string
    label: string
    defaultModelID: string
    models: GenerativeModel[]
  }>
  state: Record<string, unknown>
  assets: Array<Record<string, unknown>>
  collections?: Array<Record<string, unknown>>
  jobs: Array<Record<string, unknown>>
  recipes?: GenerativeRecipe[]
  recipeRuns?: RecipeRun[]
  brandDNA?: BrandDNAState
  campaigns?: CampaignState
  pipelines?: GenerativePipelines
  traces?: Array<Record<string, unknown>>
  storage: {
    bytes: number
  }
  commands: Record<string, string>
  serverUrl?: string
}

const RUNNING = ["submitting", "submitted", "polling"]
const PUBLIC_MODEL_ID = "adcreator"
const normalizeModelID = (_value: unknown) => PUBLIC_MODEL_ID
const legacyUnsupportedModelError = (error: unknown) =>
  /Unsupported generative model:\s*(?:openanalyst\.|adcreator-image)/i.test(String(error ?? ""))

export function GenerativeBlockView(props: {
  block: GenerativeBlock
  pending: boolean
  readonly?: boolean
  onSubmit: (blockId: string, values: Record<string, unknown>) => void | Promise<void>
  onUpload?: (blockId: string, values: { file: File; studio: string; kind?: string }) => Promise<unknown>
}) {
  let promptForm: HTMLFormElement | undefined
  let fileInput: HTMLInputElement | undefined
  const selectedModels = () =>
    typeof props.block.state.selectedModels === "object" && props.block.state.selectedModels
      ? Object.fromEntries(
          Object.entries(props.block.state.selectedModels as Record<string, unknown>).map(([key, value]) => [
            key,
            normalizeModelID(value),
          ]),
        )
      : {}
  const [activeStudio, setActiveStudio] = createSignal(String(props.block.state.activeStudio ?? "image"))
  const [selectedModelID, setSelectedModelID] = createSignal(
    normalizeModelID(
      selectedModels()[activeStudio()] ??
      props.block.studios.find((studio) => studio.id === activeStudio())?.defaultModelID ??
      "",
    ),
  )
  const [uploading, setUploading] = createSignal(false)
  const [dragging, setDragging] = createSignal(false)
  const [drawerJob, setDrawerJob] = createSignal<Record<string, unknown> | null>(null)
  const [dismissedPreviewJobID, setDismissedPreviewJobID] = createSignal("")
  const [pendingPreviewJob, setPendingPreviewJob] = createSignal<Record<string, unknown>>()
  const [draftPrompt, setDraftPrompt] = createSignal("")
  const [selectedRecipeID, setSelectedRecipeID] = createSignal(String(props.block.state.selectedRecipeID ?? props.block.recipes?.[0]?.id ?? ""))
  const [selectedScenarioID, setSelectedScenarioID] = createSignal("")
  const [recipeImportOpen, setRecipeImportOpen] = createSignal(false)
  const [recipeJson, setRecipeJson] = createSignal("")
  const [publicPackUrl, setPublicPackUrl] = createSignal("")
  const [workflowMode, setWorkflowMode] = createSignal<"recipes" | "pipelines">("recipes")
  let lastStateStudio = String(props.block.state.activeStudio ?? "image")

  const studio = createMemo(
    () => props.block.studios.find((item) => item.id === activeStudio()) ?? props.block.studios[0],
  )
  const model = createMemo(() => studio()?.models.find((item) => item.id === selectedModelID()) ?? studio()?.models[0])
  const selectedRecipe = createMemo(
    () => props.block.recipes?.find((recipe) => recipe.id === selectedRecipeID()) ?? props.block.recipes?.[0],
  )
  const selectedScenario = createMemo(
    () =>
      selectedRecipe()?.scenarios?.find((scenario) => scenario.id === selectedScenarioID()) ??
      selectedRecipe()?.scenarios?.[0],
  )
  const paramsInputs = createMemo(() => (model()?.inputs ?? []).filter((input) => input.key !== "prompt"))
  const referenceInputs = createMemo(() =>
    paramsInputs().filter((input) => /reference|frame_url/i.test(input.key)),
  )
  const advancedInputs = createMemo(() =>
    paramsInputs().filter((input) => /negative|temperature|seed|person|safety|motion|camera/i.test(input.key)),
  )
  const outputInputs = createMemo(() =>
    paramsInputs().filter((input) => !referenceInputs().includes(input) && !advancedInputs().includes(input)),
  )
  const sourceAssets = createMemo(() =>
    props.block.assets.filter(
      (asset) => String(asset.kind ?? "source") === "source" && String(asset.studio) === activeStudio(),
    ),
  )
  // Library folders + browseable images, so the user can pick existing assets as
  // references straight from a folder (in addition to upload + URL).
  const collections = createMemo(() => (props.block.collections ?? []) as Array<Record<string, unknown>>)
  const [refFolder, setRefFolder] = createSignal<string>("")
  const activeReferenceIDs = createMemo(() => {
    const value = (props.block.state as { referenceAssetIDs?: unknown }).referenceAssetIDs
    return new Set(Array.isArray(value) ? value.map(String) : [])
  })
  const libraryImages = createMemo(() =>
    props.block.assets.filter((asset) => {
      const isImage = mimeKind(String(asset.mime ?? "")) === "image" || String(asset.kind) === "image"
      const folder = refFolder()
      return isImage && (!folder || String(asset.collectionId ?? "") === folder)
    }),
  )
  const referencedLibraryAssets = createMemo(() =>
    props.block.assets.filter((asset) => activeReferenceIDs().has(String(asset.id))),
  )
  const toggleLibraryReference = (assetID: string) =>
    submit(props.block.commands.generation_use_as_reference, {}, { assetID })
  const activeJobs = createMemo(() => props.block.jobs.filter((job) => jobVisibleInStudio(job, activeStudio())))
  const activeRecipeRuns = createMemo(() => props.block.recipeRuns ?? [])
  const pollableJobs = createMemo(() =>
    props.block.jobs.filter((job) => ["submitted", "polling"].includes(String(job.status))).map((job) => String(job.id)),
  )
  const completedJobs = createMemo(() => activeJobs().filter((job) => String(job.status) === "completed" && outputUrl(job)))
  // The most recent job for the active studio — drives the big Preview panel
  // (spinner while generating, image/video when done). Chat-driven generations
  // land in `jobs` and poll automatically, so the preview tracks chat output too.
  const latestJob = createMemo(
    () => [...activeJobs()].sort((a, b) => Number(b.createdAt ?? 0) - Number(a.createdAt ?? 0))[0],
  )
  const previewJob = createMemo(() => {
    const pending = pendingPreviewJob()
    if (pending && jobVisibleInStudio(pending, activeStudio()) && String(pending.id) !== dismissedPreviewJobID()) return pending
    const job = latestJob()
    if (!job || String(job.id) === dismissedPreviewJobID()) return undefined
    if (["failed", "expired"].includes(String(job.status)) && legacyUnsupportedModelError(job.error)) return undefined
    return job
  })
  const completedRecipeRuns = createMemo(() =>
    activeRecipeRuns().filter((run) => (run.jobs ?? []).some((job) => String(job.status) === "completed" && outputUrl(job))),
  )
  const tracesForJob = (jobID: string) => (props.block.traces ?? []).filter((trace) => String(trace.jobID) === jobID)
  const savedJobIDs = createMemo(
    () =>
      new Set(
        props.block.assets
          .filter((asset) => String(asset.kind) === "output")
          .map((asset) => {
            const meta = asset.metadata as Record<string, unknown> | undefined
            return String(meta?.jobID ?? "")
          })
          .filter(Boolean),
      ),
  )

  const missingAssets = createMemo(() => {
    if (activeStudio() === "workflows") {
      const required = selectedRecipe()?.requiredAssets ?? []
      const kinds = new Set(sourceAssets().map((asset) => mimeKind(String(asset.mime))))
      return required.filter((kind) => !kinds.has(kind as "image" | "audio" | "video")).map((kind) => `${kind} reference`)
    }
    const required = model()?.requiredAssets ?? []
    if (!required.length) return [] as string[]
    const kinds = new Set(sourceAssets().map((asset) => mimeKind(String(asset.mime))))
    const hasVisual = kinds.has("image") || kinds.has("video")
    const missing: string[] = []
    if (required.includes("audio") && !kinds.has("audio")) missing.push("audio reference")
    if (required.includes("visual") && !hasVisual) missing.push("image or video reference")
    return missing
  })

  const canGenerate = createMemo(
    () =>
      props.block.policy.enabled &&
      props.block.policy.acknowledged &&
      props.block.provider.connected &&
      (activeStudio() !== "workflows" || !selectedRecipe()?.disabledReason) &&
      missingAssets().length === 0,
  )

  const disableReason = createMemo(() => {
    if (!props.block.policy.enabled) return props.block.policy.disabledReason ?? "Generative Studio is disabled."
    if (!props.block.provider.connected) return "Sign in to OpenAnalyst before generating."
    if (!props.block.policy.acknowledged) return "Acknowledge OpenAnalyst usage before generating."
    if (activeStudio() === "workflows" && selectedRecipe()?.disabledReason) return selectedRecipe()?.disabledReason ?? null
    if (missingAssets().length > 0) return `Needs ${missingAssets().join(" and ")} — upload a reference in the generation panel.`
    return null
  })

  createEffect(() => {
    const pending = pendingPreviewJob()
    if (!pending) return
    if (props.block.jobs.some((job) => String(job.id) === String(pending.id))) setPendingPreviewJob(undefined)
  })

  createEffect(() => {
    const recipe = selectedRecipe()
    if (!recipe) return
    const scenario = recipe.scenarios?.find((item) => item.id === selectedScenarioID()) ?? recipe.scenarios?.[0]
    if (scenario && scenario.id !== selectedScenarioID()) setSelectedScenarioID(scenario.id)
  })

  createEffect(() => {
    const nextStudio = String(props.block.state.activeStudio ?? "image")
    if (nextStudio === lastStateStudio) return
    lastStateStudio = nextStudio
    setActiveStudio(nextStudio)
    setSelectedModelID(
      normalizeModelID(
        selectedModels()[nextStudio] ??
        props.block.studios.find((item) => item.id === nextStudio)?.defaultModelID ??
        "",
      ),
    )
  })

  createEffect(() => {
    const jobIDs = pollableJobs()
    if (props.readonly || !jobIDs.length || !props.block.provider.connected || props.pending) return
    const timer = window.setInterval(() => {
      jobIDs.forEach((jobID) => {
        void submit(props.block.commands.generation_poll, {}, { jobID })
      })
    }, 5000)
    onCleanup(() => window.clearInterval(timer))
  })

  function submit(command: string, payload: Record<string, unknown>, extra: Record<string, unknown> = {}) {
    return props.onSubmit(props.block.id, {
      command,
      ...extra,
      payload: JSON.stringify(payload),
    })
  }

  function persistState(nextStudio = activeStudio(), nextModelID = selectedModelID()) {
    const modelID = normalizeModelID(nextModelID)
    return submit(props.block.commands.state_update, {
      activeStudio: nextStudio,
      selectedModels: { ...selectedModels(), [nextStudio]: modelID },
    })
  }

  // The control panel is actions-only; the prompt comes from the left chat. Persist
  // the current control knobs (studio, model, params) to ui_state so chat-driven
  // generation (the generate_image/generate_video tools) uses the latest settings.
  function persistParams() {
    if (!promptForm) return
    const data = new FormData(promptForm)
    return submit(props.block.commands.state_update, {
      activeStudio: activeStudio(),
      selectedModels: { ...selectedModels(), [activeStudio()]: normalizeModelID(selectedModelID()) },
      params: collectParams(promptForm),
      instructions: String(data.get("instructions") ?? ""),
      referenceAssetIDs: sourceAssets()
        .slice(0, model()?.maxReferenceAssets ?? 0)
        .map((asset) => String(asset.id)),
    })
  }

  function collectParams(form: HTMLFormElement) {
    const data = new FormData(form)
    return Object.fromEntries(
      paramsInputs().map((input) => {
        const value = data.get(input.key)
        return [
          input.key,
          input.type === "number" || input.type === "range"
            ? Number(value ?? input.default ?? 0)
            : input.type === "toggle"
              ? data.get(input.key) === "on"
              : String(value ?? input.default ?? ""),
        ]
      }),
    )
  }

  function submitGenerationForm(form: HTMLFormElement) {
    const selected = model()
    const prompt = String(new FormData(form).get("prompt") ?? "").trim()
    if (!selected || !prompt) return
    const jobID = crypto.randomUUID()
    const params = collectParams(form)
    const assetIDs = sourceAssets()
      .slice(0, selected.maxReferenceAssets ?? 0)
      .map((asset) => String(asset.id))
    const payload = {
      jobID,
      studio: activeStudio(),
      modelID: normalizeModelID(selectedModelID()),
      mode: selected.mode,
      prompt,
      params,
      assetIDs,
    }
    setDismissedPreviewJobID("")
    setPendingPreviewJob({
      id: jobID,
      studio: activeStudio(),
      modelID: payload.modelID,
      mode: selected.mode,
      prompt,
      params,
      assetIDs,
      status: "submitting",
      output: {},
      createdAt: Date.now(),
    })
    return submit(props.block.commands.generation_submit, payload)
  }

  async function uploadFile(file: File | undefined) {
    if (!file) return
    setUploading(true)
    try {
      if (props.onUpload) {
        await props.onUpload(props.block.id, { file, studio: activeStudio(), kind: "source" })
        return
      }
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader()
        reader.onerror = () => reject(reader.error)
        reader.onload = () => resolve(String(reader.result).split(",").at(1) ?? "")
        reader.readAsDataURL(file)
      })
      await submit(props.block.commands.asset_upload, {
        studio: activeStudio(),
        filename: file.name,
        mime: file.type || "application/octet-stream",
        base64,
      })
    } finally {
      setUploading(false)
      if (fileInput) fileInput.value = ""
    }
  }

  function recipeAssetIDs(recipe: GenerativeRecipe | undefined) {
    return sourceAssets()
      .slice(0, recipe?.maxReferenceAssets ?? 0)
      .map((asset) => String(asset.id))
  }

  function runRecipe(form: HTMLFormElement) {
    const recipe = selectedRecipe()
    const scenario = selectedScenario()
    if (!recipe || !scenario) return
    const data = new FormData(form)
    return submit(props.block.commands.recipe_run, {
      runID: crypto.randomUUID(),
      recipeID: recipe.id,
      scenarioID: scenario.id,
      prompt: String(data.get("workflow_prompt") ?? ""),
      params: {
        aspect_ratio: String(data.get("workflow_aspect_ratio") ?? recipe.defaultAspectRatio ?? "1:1"),
        variants: Number(data.get("workflow_variants") ?? recipe.defaultVariants ?? 1),
      },
      assetIDs: recipeAssetIDs(recipe),
    })
  }

  function importRecipeJson() {
    const raw = recipeJson().trim()
    if (!raw) return
    return submit(props.block.commands.recipe_import, {
      recipe: raw,
    })
  }

  function syncPublicPack() {
    const url = publicPackUrl().trim()
    if (!url) return
    return submit(props.block.commands.recipe_sync_public_pack, {
      url,
    })
  }

  function reusePrompt(job: Record<string, unknown>) {
    if (!promptForm) return
    const promptField = promptForm.elements.namedItem("prompt") as HTMLTextAreaElement | HTMLInputElement | null
    if (promptField) promptField.value = String(job.prompt ?? "")
    setDraftPrompt(String(job.prompt ?? ""))
    const params = (job.params as Record<string, unknown>) ?? {}
    paramsInputs().forEach((input) => {
      const field = promptForm!.elements.namedItem(input.key) as HTMLInputElement | HTMLSelectElement | null
      if (!field) return
      const value = params[input.key]
      if (value === undefined) return
      if ("type" in field && field.type === "checkbox")
        (field as HTMLInputElement).checked = Boolean(value)
      else field.value = String(value)
    })
    setDrawerJob(null)
    promptField?.focus()
  }

  return (
    <section
      data-generative-block
      class="xl:col-span-2 overflow-hidden rounded-lg border border-border-weak-base bg-background-base"
    >
      <Show
        when={props.block.studios.length > 0}
        fallback={
          <section class="rounded-lg bg-background-base p-8 text-center">
            <div class="text-15-medium text-text-strong">Generative Studio isn't available on this workspace.</div>
            <div class="mt-1 text-13-regular text-text-base">Contact your admin to enable it.</div>
          </section>
        }
      >
        <div class="flex flex-col gap-4 border-b border-border-weak-base px-5 py-4 lg:flex-row lg:items-center lg:justify-between">
          <div class="min-w-0">
            <Show when={props.block.title}>
              <h2 class="text-18-semibold text-text-strong">{props.block.title}</h2>
            </Show>
            <div class="mt-1 text-13-regular text-text-base">
              Image, video, lip sync, and cinema generation · {formatBytes(props.block.storage.bytes)} stored locally
            </div>
          </div>
          <div class="flex flex-col items-end gap-2">
            <div class="flex flex-wrap gap-2">
              <For each={props.block.studios}>
                {(item) => (
                  <button
                    type="button"
                    data-generative-studio={item.id}
                    data-selected={activeStudio() === item.id ? "true" : "false"}
                    class={`rounded-md border px-3 py-2 text-13-medium transition-all ${
                      activeStudio() === item.id
                        ? "border-border-strong font-semibold shadow-sm"
                        : "border-border-weak-base text-text-base hover:border-text-strong"
                    }`}
                    style={
                      activeStudio() === item.id
                        ? {
                            color: "var(--oa-plugin-accent, #4f46e5)",
                            "border-color": "var(--oa-plugin-accent, #6366f1)",
                            background: "color-mix(in srgb, var(--oa-plugin-accent, #6366f1) 12%, transparent)",
                          }
                        : undefined
                    }
                    on:click={() => {
                      setActiveStudio(item.id)
                      const nextModelID = selectedModels()[item.id] ?? item.defaultModelID
                      const normalized = normalizeModelID(nextModelID)
                      setSelectedModelID(normalized)
                      setDismissedPreviewJobID("")
                      void persistState(item.id, normalized)
                    }}
                  >
                    {item.label}
                  </button>
                )}
              </For>
            </div>
            {/* No model name/badge — users don't pick or see models; the backend
                selects the model and the call uses the user's OpenAnalyst auth. */}
          </div>
        </div>

        <div class="grid grid-cols-1 gap-5 p-5 xl:grid-cols-[minmax(0,1fr)_320px]">
          <div class="flex min-w-0 flex-col gap-4">
            <Show when={!props.block.policy.enabled}>
              <div class="rounded-lg border border-border-weak-base bg-background-secondary p-4 text-14-regular text-text-base">
                {props.block.policy.disabledReason ?? "Generative Studio is disabled."}
              </div>
            </Show>

            <Show
              when={activeStudio() === "workflows"}
              fallback={
                <>
                  <Show when={missingAssets().length > 0}>
                    <div class="rounded-md border border-border-weak-base bg-background-secondary px-3 py-2 text-12-regular text-text-base">
                      Needs {missingAssets().join(" and ")} — upload a reference in the generation panel.
                    </div>
                  </Show>

                  <form
                    ref={(el) => {
                      promptForm = el
                    }}
                    class="overflow-hidden rounded-xl border border-border-weak-base bg-background-secondary"
                    on:submit={(event) => {
                      event.preventDefault()
                      if (promptForm) void submitGenerationForm(promptForm)
                    }}
                    on:change={() => void persistParams()}
                  >
                    <div class="border-b border-border-weak-base bg-background-base px-4 py-3">
                      <div class="flex flex-wrap items-start justify-between gap-3">
                        <div class="min-w-0">
                          <div class="text-15-medium text-text-strong">Generation panel</div>
                          <div class="mt-1 text-12-regular text-text-base">
                            Create here or ask chat to use the same saved knobs.
                          </div>
                        </div>
                        <div class="flex flex-wrap gap-2">
                          <Button type="button" variant="secondary" size="small" onClick={() => fileInput?.click()}>
                            Add reference
                          </Button>
                          <Button type="button" variant="ghost" size="small" onClick={() => void persistParams()}>
                            Save knobs
                          </Button>
                        </div>
                      </div>
                    </div>
                    {/* Model picker removed — no OpenAnalyst-branded model names shown.
                        The studio's default model is used implicitly; the user only
                        sets the prompt (chat) + params. */}

                    <div class="flex flex-col gap-5 p-4">
                      <ControlSection title="Prompt" description="Use a direct brief here, or ask chat to create with this plugin.">
                        <textarea
                          name="prompt"
                          required
                          value={draftPrompt()}
                          placeholder={activeStudio() === "video" ? "Describe the video scene, motion, camera, product, and ending..." : "Describe the image, product, subject, mood, copy-safe constraints, and style..."}
                          class="min-h-32 w-full resize-y rounded-lg border border-border-weak-base bg-background-base px-3 py-2 text-14-regular text-text-strong outline-none transition-all focus:border-[var(--oa-plugin-accent,#6366f1)] focus:shadow-[0_0_0_3px_color-mix(in_srgb,var(--oa-plugin-accent,#6366f1)_14%,transparent)]"
                          onInput={(event) => setDraftPrompt(event.currentTarget.value)}
                        />
                      </ControlSection>

                      <Show when={outputInputs().length > 0}>
                        <ControlSection title="Output" description="Size, aspect, count, variants, and final delivery format.">
                          <div class="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                            <For each={outputInputs()}>{(input) => <GenerativeField input={input} />}</For>
                          </div>
                        </ControlSection>
                      </Show>

                      <ControlSection
                        title="References"
                        description="Guide generation with reference images, video, or audio — upload from this workspace or paste URLs below."
                      >
                        <div class="flex flex-col gap-3">
                          {/* Active references — uploads + library picks in one wrapping
                              grid so any mix stays tidy. Library picks toggle off; uploads
                              are deleted. */}
                          <Show
                            when={
                              sourceAssets().slice(0, model()?.maxReferenceAssets ?? 0).length > 0 ||
                              referencedLibraryAssets().length > 0
                            }
                          >
                            <div class="grid grid-cols-3 gap-2 sm:grid-cols-4 lg:grid-cols-5">
                              <For each={sourceAssets().slice(0, model()?.maxReferenceAssets ?? 0)}>
                                {(asset) => (
                                  <ReferenceThumb
                                    asset={asset}
                                    onRemove={() => submit(props.block.commands.asset_delete, {}, { assetID: String(asset.id) })}
                                  />
                                )}
                              </For>
                              <For each={referencedLibraryAssets()}>
                                {(asset) => (
                                  <ReferenceThumb asset={asset} onRemove={() => toggleLibraryReference(String(asset.id))} />
                                )}
                              </For>
                            </div>
                          </Show>

                          {/* Add references — upload + URL + a folder→image picker so the
                              user can reuse existing Library images as references quickly. */}
                          <div class="flex flex-col gap-2.5 rounded-lg border border-border-weak-base bg-background-base p-3">
                            <button
                              type="button"
                              onClick={() => fileInput?.click()}
                              class="flex items-center justify-center gap-2 rounded-md border border-dashed border-border-weak-base px-3 py-2.5 text-13-medium text-text-strong transition-all hover:border-[var(--oa-plugin-accent,#6366f1)]"
                            >
                              <Icon name="cloud-upload" size="small" />
                              {uploading() ? "Uploading…" : "Upload reference"}
                              <span class="text-11-regular text-text-base">· image, video, or audio</span>
                            </button>
                            <Show when={referenceInputs().length > 0}>
                              <div class="flex flex-col gap-2">
                                <For each={referenceInputs()}>{(input) => <GenerativeField input={input} />}</For>
                              </div>
                            </Show>
                            {/* Add from Library — pick existing images from a folder. */}
                            <Show when={libraryImages().length > 0 || collections().length > 0}>
                              <div class="flex flex-col gap-2 border-t border-border-weak-base pt-2.5">
                                <div class="flex items-center justify-between gap-2">
                                  <span class="text-12-medium text-text-strong">Add from Library</span>
                                  <select
                                    value={refFolder()}
                                    onChange={(event) => setRefFolder(event.currentTarget.value)}
                                    class="rounded-md border border-border-weak-base bg-background-base px-2 py-1 text-12-regular text-text-base"
                                  >
                                    <option value="">All folders</option>
                                    <For each={collections()}>
                                      {(collection) => (
                                        <option value={String(collection.id)}>{String(collection.name ?? collection.id)}</option>
                                      )}
                                    </For>
                                  </select>
                                </div>
                                <Show
                                  when={libraryImages().length > 0}
                                  fallback={<p class="text-11-regular text-text-base">No images in this folder yet.</p>}
                                >
                                  <div class="grid max-h-52 grid-cols-4 gap-2 overflow-auto sm:grid-cols-6">
                                    <For each={libraryImages()}>
                                      {(asset) => {
                                        const preview = String(asset.previewUrl ?? asset.remoteUrl ?? "")
                                        const picked = () => activeReferenceIDs().has(String(asset.id))
                                        return (
                                          <button
                                            type="button"
                                            onClick={() => toggleLibraryReference(String(asset.id))}
                                            title={String(asset.filename)}
                                            class="group relative aspect-square overflow-hidden rounded-lg border-2 bg-background-base transition-all"
                                            classList={{
                                              "border-[var(--oa-plugin-accent,#6366f1)]": picked(),
                                              "border-transparent hover:border-border-base": !picked(),
                                            }}
                                          >
                                            <Show
                                              when={preview}
                                              fallback={
                                                <span class="flex h-full w-full items-center justify-center p-1 text-center text-9-regular text-text-base">
                                                  {String(asset.filename)}
                                                </span>
                                              }
                                            >
                                              <img src={preview} alt="" loading="lazy" class="h-full w-full object-cover" />
                                            </Show>
                                            <Show when={picked()}>
                                              <span class="absolute right-1 top-1 flex size-4 items-center justify-center rounded-full bg-[var(--oa-plugin-accent,#6366f1)] text-10-medium leading-none text-white">
                                                ✓
                                              </span>
                                            </Show>
                                          </button>
                                        )
                                      }}
                                    </For>
                                  </div>
                                </Show>
                              </div>
                            </Show>
                          </div>
                        </div>
                      </ControlSection>

                      <Show when={advancedInputs().length > 0}>
                        <ControlSection title="Advanced" description="Safety, seed, temperature, camera, and negative prompt controls.">
                          <div class="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                            <For each={advancedInputs()}>{(input) => <GenerativeField input={input} />}</For>
                          </div>
                        </ControlSection>
                      </Show>

                    <label class="flex flex-col gap-2 text-12-medium text-text-base">
                      <span>Extra instructions & brand context</span>
                      <textarea
                        name="instructions"
                        placeholder="Brand voice, style, audience, do's and don'ts... applied to chat-driven generations as system context."
                        value={String(props.block.state.instructions ?? "")}
                        class="min-h-24 w-full resize-y rounded-lg border border-border-weak-base bg-background-base px-3 py-2 text-13-regular text-text-strong outline-none transition-all focus:border-[var(--oa-plugin-accent,#6366f1)] focus:shadow-[0_0_0_3px_color-mix(in_srgb,var(--oa-plugin-accent,#6366f1)_14%,transparent)]"
                      />
                    </label>

                    <div class="flex flex-col gap-2 border-t border-border-weak-base pt-4 sm:flex-row sm:items-center sm:justify-between">
                      <div class="text-12-regular text-text-base">
                        <Show when={disableReason()} fallback="Ready for direct generation or chat-driven creation.">
                          {(reason) => reason()}
                        </Show>
                      </div>
                      <Button
                        type="submit"
                        variant="primary"
                        size="large"
                        disabled={!canGenerate() || props.pending || !draftPrompt().trim()}
                      >
                        Generate
                      </Button>
                    </div>
                    </div>
                  </form>

                  <GenerationQueue
                    jobs={activeJobs()}
                    pending={props.pending}
                    savedJobIDs={savedJobIDs()}
                    onSubmit={submit}
                    onOpen={setDrawerJob}
                    commands={props.block.commands}
                  />

                  <GenerationGallery
                    jobs={completedJobs()}
                    commands={props.block.commands}
                    onSubmit={submit}
                    onOpen={setDrawerJob}
                  />
                </>
              }
            >
              <div class="flex flex-col gap-4">
                <RecipeStudio
                  recipes={props.block.recipes ?? []}
                  runs={activeRecipeRuns()}
                  completedRuns={completedRecipeRuns()}
                  pipelines={props.block.pipelines}
                  mode={workflowMode()}
                  selectedRecipe={selectedRecipe()}
                  selectedScenario={selectedScenario()}
                  selectedRecipeID={selectedRecipeID()}
                  selectedScenarioID={selectedScenarioID()}
                  canGenerate={canGenerate()}
                  pending={props.pending}
                  disableReason={disableReason()}
                  recipeImportOpen={recipeImportOpen()}
                  recipeJson={recipeJson()}
                  publicPackUrl={publicPackUrl()}
                  savedJobIDs={savedJobIDs()}
                  commands={props.block.commands}
                  readonly={props.readonly}
                  onMode={setWorkflowMode}
                  onSelectRecipe={(recipe) => {
                    setSelectedRecipeID(recipe.id)
                    setSelectedScenarioID(recipe.scenarios?.[0]?.id ?? "")
                    void submit(props.block.commands.state_update, { selectedRecipeID: recipe.id })
                  }}
                  onSelectScenario={setSelectedScenarioID}
                  onRun={runRecipe}
                  onToggleImport={() => setRecipeImportOpen(!recipeImportOpen())}
                  onRecipeJson={setRecipeJson}
                  onImportJson={() => void importRecipeJson()}
                  onPublicPackUrl={setPublicPackUrl}
                  onSyncPublicPack={() => void syncPublicPack()}
                  onSubmit={submit}
                  onOpen={setDrawerJob}
                />
              </div>
            </Show>
          </div>

          <aside class="flex min-w-0 flex-col gap-4">
            {/* Preview lives in the RIGHT panel: shows the generating/polling spinner
                while a job runs (incl. chat-driven), then the finished image/video. */}
            <Show when={activeStudio() !== "workflows"}>
              <PreviewPanel
                job={previewJob()}
                pending={props.pending}
                commands={props.block.commands}
                savedJobIDs={savedJobIDs()}
                onSubmit={submit}
                onOpen={setDrawerJob}
                onClosePreview={() => setDismissedPreviewJobID(String(previewJob()?.id ?? ""))}
              />
            </Show>
            <div class="flex items-center gap-2 rounded-lg border border-border-weak-base bg-background-secondary p-3">
              <span
                class={`inline-block h-1.5 w-1.5 rounded-full ${
                  props.block.provider.connected ? "bg-icon-success-active" : "bg-icon-weak-base"
                }`}
              />
              <span class="text-13-medium text-text-strong">OpenAnalyst</span>
              <span class="text-12-regular text-text-base">
                {props.block.provider.connected ? "Session active" : "Sign in required"}
              </span>
            </div>

            <Show
              when={props.block.policy.acknowledged}
              fallback={
                <div class="rounded-lg border border-border-weak-base bg-background-secondary p-4">
                  <h3 class="text-15-medium text-text-strong">Usage</h3>
                  <p class="mt-2 text-13-regular text-text-base">
                    Generations run through OpenAnalyst for the active workspace.
                  </p>
                  <div class="mt-3">
                    <Button
                      type="button"
                      variant="primary"
                      onClick={() => submit(props.block.commands.policy_acknowledge, {})}
                    >
                      Acknowledge
                    </Button>
                  </div>
                </div>
              }
            >
              <div class="flex items-center justify-between gap-2 rounded-lg border border-border-weak-base bg-background-secondary p-3">
                <span class="text-12-regular text-text-base">Usage acknowledged</span>
                <Tooltip value="Revoke workspace usage acknowledgment">
                  <button
                    type="button"
                    class="text-12-medium text-text-base hover:text-text-strong"
                    onClick={() => {
                      if (
                        window.confirm(
                          "Revoke OpenAnalyst usage acknowledgment? Generation will be disabled until re-acknowledged.",
                        )
                      ) {
                        void submit(props.block.commands.policy_acknowledge, { revoke: true })
                      }
                    }}
                  >
                    Revoke
                  </button>
                </Tooltip>
              </div>
            </Show>

            {/* Hidden file input powering the LEFT References "Upload reference" +
                drag/drop. The visible reference list now lives in the left panel,
                so the right panel stays a clean Preview-first surface. */}
            <input
              ref={(el) => {
                fileInput = el
              }}
              type="file"
              class="hidden"
              on:change={() => void uploadFile(fileInput?.files?.[0])}
            />
            <BrandDNAWorkspace
              brandDNA={props.block.brandDNA}
              campaigns={props.block.campaigns}
              commands={props.block.commands}
              onSubmit={submit}
            />
          </aside>
        </div>

        <Show when={drawerJob()}>
          {(job) => (
            <div
              ref={(el) => {
                if (el) queueMicrotask(() => el.focus())
              }}
              role="dialog"
              aria-modal="true"
              tabIndex={-1}
              class="fixed inset-0 z-50 flex items-end justify-center bg-background-base/80 backdrop-blur-sm sm:items-center"
              onClick={(event) => {
                if (event.target === event.currentTarget) setDrawerJob(null)
              }}
              onKeyDown={(event) => {
                if (event.key === "Escape") setDrawerJob(null)
              }}
            >
              <div class="max-h-[85vh] w-full max-w-2xl overflow-auto rounded-t-lg border border-border-weak-base bg-background-base p-5 sm:rounded-lg">
                <div class="flex items-start justify-between gap-3">
                  <div class="min-w-0">
                    <h3 class="text-15-medium text-text-strong">
                      {String(job().prompt ?? job().modelID ?? "Generation")}
                    </h3>
                    <div class="mt-1 text-12-regular text-text-base">{String(job().modelID)}</div>
                  </div>
                  <Tooltip value="Close preview">
                    <button
                      type="button"
                      aria-label="Close preview"
                      data-generative-drawer-close
                      class="flex size-8 items-center justify-center rounded-md border border-border-weak-base bg-background-base text-icon-base hover:bg-background-secondary hover:text-icon-strong"
                      onClick={() => setDrawerJob(null)}
                    >
                      <Icon name="close" size="small" />
                    </button>
                  </Tooltip>
                </div>
                <div class="mt-4">
                  <OutputPreview job={job()} commands={props.block.commands} onSubmit={submit} />
                </div>
                <Show when={Object.keys((job().params as Record<string, unknown>) ?? {}).length > 0}>
                  <div class="mt-4 rounded-md border border-border-weak-base bg-background-secondary p-3">
                    <div class="text-12-medium text-text-base">Params</div>
                    <div class="mt-2 grid grid-cols-1 gap-1 text-12-regular text-text-base sm:grid-cols-2">
                      <For each={Object.entries((job().params as Record<string, unknown>) ?? {})}>
                        {([k, v]) => (
                          <div class="flex justify-between gap-2">
                            <span>{k}</span>
                            <span class="text-text-strong">{formatParam(v)}</span>
                          </div>
                        )}
                      </For>
                    </div>
                  </div>
                </Show>
                <JobTracePanel traces={tracesForJob(String(job().id))} />
                <div class="mt-4 flex flex-wrap gap-2">
                  <a href={outputUrl(job())} download={String(job().id)} class="inline-flex">
                    <Button type="button" variant="secondary">
                      Download
                    </Button>
                  </a>
                  <Show
                    when={savedJobIDs().has(String(job().id))}
                    fallback={
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() => {
                          void submit(props.block.commands.generation_keep, {}, { jobID: String(job().id) })
                          setDrawerJob(null)
                        }}
                      >
                        Save to library
                      </Button>
                    }
                  >
                    <Button type="button" variant="secondary" disabled>
                      Saved
                    </Button>
                  </Show>
                  <Button type="button" variant="ghost" onClick={() => reusePrompt(job())}>
                    Reuse prompt
                  </Button>
                </div>
              </div>
            </div>
          )}
        </Show>
      </Show>
    </section>
  )
}

function ControlSection(props: { title: string; description?: string; children: JSX.Element }) {
  return (
    <section class="rounded-lg border border-border-weak-base bg-background-base p-4">
      <div class="mb-3 flex flex-col gap-1">
        <h3 class="text-14-medium text-text-strong">{props.title}</h3>
        <Show when={props.description}>
          {(description) => <p class="text-12-regular text-text-base">{description()}</p>}
        </Show>
      </div>
      {props.children}
    </section>
  )
}

function JobTracePanel(props: { traces: Array<Record<string, unknown>> }) {
  return (
    <Show when={props.traces.length > 0}>
      <div class="mt-4 rounded-md border border-border-weak-base bg-background-secondary p-3">
        <div class="flex items-center justify-between gap-3">
          <div class="text-12-medium text-text-base">Timing Trace</div>
          <div class="text-11-regular text-text-base">{props.traces.length} events</div>
        </div>
        <div class="mt-3 flex flex-col gap-2">
          <For each={props.traces}>
            {(trace) => (
              <div class="rounded-md border border-border-weak-base bg-background-base p-2">
                <div class="flex flex-wrap items-center gap-2">
                  <span class="text-12-medium text-text-strong">{String(trace.event)}</span>
                  <StatusChip status={String(trace.status ?? "trace")} />
                  <span class="text-11-regular text-text-base">{formatTraceTime(trace.createdAt)}</span>
                </div>
                <div class="mt-2 flex flex-wrap gap-1">
                  <For each={traceBadges(trace)}>
                    {(item) => (
                      <span class="rounded-md border border-border-weak-base px-2 py-1 text-11-regular text-text-base">
                        {item}
                      </span>
                    )}
                  </For>
                </div>
              </div>
            )}
          </For>
        </div>
      </div>
    </Show>
  )
}

function GenerationQueue(props: {
  jobs: Array<Record<string, unknown>>
  pending: boolean
  savedJobIDs: Set<string>
  commands: Record<string, string>
  onSubmit: CommandSubmit
  onOpen: (job: Record<string, unknown>) => void
}) {
  return (
    <div class="rounded-lg border border-border-weak-base bg-background-secondary p-4">
      <div class="mb-3 flex items-center justify-between gap-3">
        <h3 class="text-15-medium text-text-strong">Job Queue</h3>
        <span class="text-12-regular text-text-base">{props.jobs.length} jobs</span>
      </div>
      <div class="flex flex-col gap-2">
        <For each={props.jobs} fallback={<QueueEmptyState />}>
          {(job) => {
            const status = () => String(job.status)
            const hasOutput = () => typeof (job.output as Record<string, unknown> | undefined)?.url === "string"
            const running = () => RUNNING.includes(status())
            const saved = () => props.savedJobIDs.has(String(job.id))
            return (
              <div
                data-generative-queue-row={String(job.id)}
                class="rounded-md border border-border-weak-base bg-background-base p-3 transition-colors"
              >
                <div class="flex items-start justify-between gap-3">
                  <div class="min-w-0">
                    <div class="truncate text-14-medium text-text-strong">
                      {String(job.prompt ?? job.modelID ?? "Generation")}
                    </div>
                    <div class="mt-1 flex items-center gap-2">
                      <StatusChip status={status()} />
                      <span class="text-12-regular text-text-base">{String(job.modelID)}</span>
                    </div>
                  </div>
                  <div class="flex shrink-0 gap-2">
                    <Show when={running()}>
                      <Button
                        type="button"
                        variant="secondary"
                        disabled={props.pending}
                        onClick={() => props.onSubmit(props.commands.generation_poll, {}, { jobID: String(job.id) })}
                      >
                        Poll
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => props.onSubmit(props.commands.generation_cancel, {}, { jobID: String(job.id) })}
                      >
                        Cancel
                      </Button>
                    </Show>
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => props.onSubmit(props.commands.generation_delete, {}, { jobID: String(job.id) })}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
                <Show when={running() && !hasOutput()}>
                  <div class={`mt-3 w-full overflow-hidden rounded-md bg-background-secondary ${aspectClass(job)}`}>
                    <div class="h-full w-full animate-pulse bg-gradient-to-r from-background-secondary via-background-base to-background-secondary" />
                  </div>
                </Show>
                <Show when={hasOutput()}>
                  <div class="mt-3 flex flex-col gap-3">
                    <OutputPreview job={job} commands={props.commands} onSubmit={props.onSubmit} />
                    <JobActions
                      job={job}
                      saved={saved()}
                      commands={props.commands}
                      onSubmit={props.onSubmit}
                      onOpen={props.onOpen}
                    />
                  </div>
                </Show>
                <Show when={job.error}>
                  <ErrorBlock error={job.error} />
                </Show>
              </div>
            )
          }}
        </For>
      </div>
    </div>
  )
}

function GenerationGallery(props: {
  jobs: Array<Record<string, unknown>>
  commands?: Record<string, string>
  onSubmit?: CommandSubmit
  onOpen: (job: Record<string, unknown>) => void
}) {
  return (
    <div class="rounded-lg border border-border-weak-base bg-background-secondary p-4">
      <div class="mb-3 flex items-center justify-between gap-3">
        <h3 class="text-15-medium text-text-strong">History Gallery</h3>
        <span class="text-12-regular text-text-base">{props.jobs.length} saved outputs</span>
      </div>
      <div class="grid grid-cols-1 gap-3 md:grid-cols-2">
        <For each={props.jobs} fallback={<GalleryEmptyState />}>
          {(job) => (
            <button
              type="button"
              class="overflow-hidden rounded-md border border-border-weak-base bg-background-base text-left text-text-strong"
              onClick={(event) => {
                if (event.metaKey || event.ctrlKey) {
                  window.open(outputUrl(job), "_blank")
                  return
                }
                props.onOpen(job)
              }}
            >
              <OutputPreview job={job} compact commands={props.commands} onSubmit={props.onSubmit} />
              <div class="truncate px-3 py-2 text-13-medium">{String(job.prompt ?? job.modelID ?? "Generation")}</div>
            </button>
          )}
        </For>
      </div>
    </div>
  )
}

// The big result Preview at the top of the Generate panel. Shows a spinner while
// the latest generation is in flight (incl. chat-driven ones), the image/video
// when it completes, and a "Use as reference" action that feeds it back into the
// next chat prompt (image-to-image refine). The prompt itself comes from chat.
function PreviewPanel(props: {
  job?: Record<string, unknown>
  pending: boolean
  commands: Record<string, string>
  savedJobIDs: Set<string>
  onSubmit: CommandSubmit
  onOpen: (job: Record<string, unknown>) => void
  onClosePreview: () => void
}) {
  const dialog = useDialog()
  const status = () => String(props.job?.status ?? "")
  const generating = () => Boolean(props.job) && ["draft", "submitting", "submitted", "polling"].includes(status())
  const done = () => Boolean(props.job) && status() === "completed" && Boolean(outputUrl(props.job!))
  const failed = () => Boolean(props.job) && ["failed", "expired"].includes(status())
  const saved = () => Boolean(props.job) && props.savedJobIDs.has(String(props.job!.id))
  const outOfCredits = () => failed() && isInsufficientCredits(props.job?.error)
  const openBilling = () => {
    void import("@/components/settings-v2").then((mod) => {
      dialog.show(() => <mod.DialogSettings initialTab="billing" />)
    })
  }
  return (
    <div class="rounded-lg border border-border-weak-base bg-background-secondary p-4">
      <div class="mb-3 flex items-center justify-between gap-3">
        <h3 class="text-15-medium text-text-strong">Preview</h3>
        <Show when={props.job}>
          <div class="flex items-center gap-2">
            <span class="text-12-regular capitalize text-text-base">{status()}</span>
            <Show when={done()}>
              <Tooltip value="Expand preview">
                <button
                  type="button"
                  aria-label="Expand preview"
                  class="flex size-7 items-center justify-center rounded-md border border-border-weak-base bg-background-base text-icon-base hover:bg-background-secondary hover:text-icon-strong"
                  onClick={() => props.job && props.onOpen(props.job)}
                >
                  <Icon name="expand" size="small" />
                </button>
              </Tooltip>
              <Tooltip value="Download output">
                <a
                  aria-label="Download output"
                  href={props.job ? outputUrl(props.job) : undefined}
                  download={String(props.job?.id ?? "generation")}
                  class="flex size-7 items-center justify-center rounded-md border border-border-weak-base bg-background-base text-icon-base hover:bg-background-secondary hover:text-icon-strong"
                >
                  <Icon name="download" size="small" />
                </a>
              </Tooltip>
            </Show>
            <Tooltip value="Close preview">
              <button
                type="button"
                aria-label="Close preview"
                data-generative-preview-close
                class="flex size-7 items-center justify-center rounded-md border border-border-weak-base bg-background-base text-icon-base hover:bg-background-secondary hover:text-icon-strong"
                onClick={props.onClosePreview}
              >
                <Icon name="close-small" size="small" />
              </button>
            </Tooltip>
          </div>
        </Show>
      </div>
      <Show
        when={props.job}
        fallback={
          <div class="flex min-h-48 flex-col items-center justify-center gap-2 rounded-md border border-dashed border-border-weak-base bg-background-base p-6 text-center">
            <div class="text-13-medium text-text-strong">Your generation appears here</div>
            <div class="max-w-md text-12-regular text-text-base">
              Type a prompt or ask chat to create an image or video. The output renders here as it generates.
            </div>
          </div>
        }
      >
        {(job) => (
          <div class="flex flex-col gap-3">
            <Show when={generating()}>
              <GenerationStageVisual job={job()} />
            </Show>
            <Show when={failed()}>
              <div class="flex min-h-32 flex-col items-center justify-center gap-2 rounded-md border border-border-weak-base bg-background-base p-6 text-center">
                <div class="text-13-medium text-text-strong">
                  {outOfCredits() ? "Insufficient credits" : humaniseError(job().error).title}
                </div>
                <div class="max-w-md text-12-regular text-text-base">
                  {outOfCredits()
                    ? String(job().error ?? "You don't have enough credits for this generation.")
                    : legacyUnsupportedModelError(job().error)
                      ? "This saved job used an older model route. New requests now use Ad Creator."
                      : "Adjust the prompt or references and generate again."}
                </div>
                <Show when={outOfCredits()}>
                  <Button type="button" variant="primary" size="small" onClick={openBilling}>
                    Add credits
                  </Button>
                </Show>
              </div>
            </Show>
            <Show when={done()}>
              <OutputPreview job={job()} commands={props.commands} onSubmit={props.onSubmit} />
              {/* Primary actions only — Download + Expand live in the header icons
                  above, so they are NOT repeated here (no duplicate options). */}
              <div class="flex flex-wrap gap-2">
                <Show
                  when={!saved()}
                  fallback={
                    <Button type="button" variant="secondary" disabled>
                      Saved
                    </Button>
                  }
                >
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => props.onSubmit(props.commands.generation_keep, {}, { jobID: String(job().id) })}
                  >
                    Save to library
                  </Button>
                </Show>
                <Button
                  type="button"
                  variant="primary"
                  onClick={() =>
                    props.onSubmit(props.commands.generation_use_as_reference, {}, { jobID: String(job().id) })
                  }
                >
                  Use as reference
                </Button>
              </div>
              <p class="text-12-regular text-text-base">
                Use as reference keeps this result and applies it to your next chat prompt — it appears in References on the
                left. Describe the change to refine it.
              </p>
            </Show>
          </div>
        )}
      </Show>
    </div>
  )
}

export function GenerationStageVisual(props: { job: Record<string, unknown> }) {
  const status = () => String(props.job.status ?? "submitted")
  const isVideo = () => outputKind(props.job) === "video"
  const label = () =>
    status() === "submitting"
      ? "Preparing request"
      : status() === "submitted" || status() === "polling"
        ? `Generating ${isVideo() ? "video" : "image"} preview`
        : `Generating ${isVideo() ? "video" : "image"}`
  const progress = () => (status() === "submitting" ? "35%" : status() === "submitted" ? "62%" : "78%")

  return (
    <div class={`min-h-56 overflow-hidden rounded-lg border border-border-weak-base bg-background-base ${aspectClass(props.job)}`}>
      <div class="flex h-full min-h-56 flex-col justify-between gap-4 p-5">
        <div class="flex items-center justify-between gap-3">
          <div>
            <div class="text-13-medium text-text-strong">{label()}</div>
            <div class="mt-1 line-clamp-2 text-12-regular text-text-base">{String(props.job.prompt ?? "")}</div>
            <div class="mt-2 text-11-regular uppercase tracking-wide text-text-base">
              {status() === "submitting" ? "Submitting" : status() === "submitted" ? "Queued" : "Polling output"}
            </div>
          </div>
          <span
            aria-hidden="true"
            class="size-9 shrink-0 animate-spin rounded-full border-[3px] border-border-weak-base"
            style={{ "border-top-color": "var(--oa-plugin-accent, #6366f1)" }}
          />
        </div>
        <div class="grid flex-1 grid-cols-3 gap-3">
          <div class="col-span-2 min-h-32 animate-pulse rounded-lg bg-gradient-to-br from-background-secondary via-background-base to-background-secondary" />
          <div class="flex flex-col gap-3">
            <div class="h-14 animate-pulse rounded-lg bg-background-secondary" />
            <div class="h-14 animate-pulse rounded-lg bg-background-secondary" />
            <div class="h-14 animate-pulse rounded-lg bg-background-secondary" />
          </div>
        </div>
        <div class="h-1.5 overflow-hidden rounded-full bg-background-secondary">
          <div
            class="h-full animate-pulse rounded-full transition-all"
            style={{ background: "var(--oa-plugin-accent, #6366f1)", width: progress() }}
          />
        </div>
      </div>
    </div>
  )
}

function JobActions(props: {
  job: Record<string, unknown>
  saved: boolean
  commands: Record<string, string>
  onSubmit: CommandSubmit
  onOpen?: (job: Record<string, unknown>) => void
}) {
  return (
    <div class="flex flex-wrap gap-2">
      <a href={outputUrl(props.job)} download={String(props.job.id)} class="inline-flex">
        <Button type="button" variant="secondary">
          Download
        </Button>
      </a>
      <Show
        when={props.saved}
        fallback={
          <Button
            type="button"
            variant="secondary"
            onClick={() => props.onSubmit(props.commands.generation_keep, {}, { jobID: String(props.job.id) })}
          >
            Save to library
          </Button>
        }
      >
        <Button type="button" variant="secondary" disabled>
          Saved
        </Button>
      </Show>
      <Show when={props.onOpen}>
        {(onOpen) => (
          <Button type="button" variant="ghost" onClick={() => onOpen()(props.job)}>
            Open
          </Button>
        )}
      </Show>
    </div>
  )
}

function RecipeStudio(props: {
  recipes: GenerativeRecipe[]
  runs: RecipeRun[]
  completedRuns: RecipeRun[]
  pipelines?: GenerativePipelines
  mode: "recipes" | "pipelines"
  selectedRecipe?: GenerativeRecipe
  selectedScenario?: RecipeScenario
  selectedRecipeID: string
  selectedScenarioID: string
  canGenerate: boolean
  pending: boolean
  disableReason: string | null
  recipeImportOpen: boolean
  recipeJson: string
  publicPackUrl: string
  savedJobIDs: Set<string>
  commands: Record<string, string>
  readonly?: boolean
  onMode: (mode: "recipes" | "pipelines") => void
  onSelectRecipe: (recipe: GenerativeRecipe) => void
  onSelectScenario: (scenarioID: string) => void
  onRun: (form: HTMLFormElement) => void | Promise<void>
  onToggleImport: () => void
  onRecipeJson: (value: string) => void
  onImportJson: () => void
  onPublicPackUrl: (value: string) => void
  onSyncPublicPack: () => void
  onSubmit: CommandSubmit
  onOpen: (job: Record<string, unknown>) => void
}) {
  let form: HTMLFormElement | undefined
  const recipe = () => props.selectedRecipe
  const scenario = () => props.selectedScenario
  const minVariants = () => recipe()?.minVariants ?? 1
  const maxVariants = () => recipe()?.maxVariants ?? 4
  const defaultVariants = () => recipe()?.defaultVariants ?? 1
  const latestRun = createMemo(() => props.runs[0])
  return (
    <>
      <div class="flex flex-wrap gap-2 rounded-lg border border-border-weak-base bg-background-secondary p-2">
        <Button type="button" variant={props.mode === "recipes" ? "primary" : "secondary"} onClick={() => props.onMode("recipes")}>
          Recipes
        </Button>
        <Button type="button" variant={props.mode === "pipelines" ? "primary" : "secondary"} onClick={() => props.onMode("pipelines")}>
          Pipeline Designer
        </Button>
      </div>
      <Show
        when={props.mode === "recipes"}
        fallback={
          <PipelineDesigner
            pipelines={props.pipelines}
            commands={props.pipelines?.commands ?? props.commands}
            pending={props.pending}
            readonly={props.readonly}
            onSubmit={props.onSubmit}
          />
        }
      >
      <div class="rounded-lg border border-border-weak-base bg-background-secondary p-4">
        <div class="mb-3 flex items-center justify-between gap-3">
          <h3 class="text-15-medium text-text-strong">Workflows</h3>
          <Button type="button" variant="secondary" onClick={props.onToggleImport}>
            Import
          </Button>
        </div>
        <div class="grid grid-cols-1 gap-2 md:grid-cols-3">
          <For each={props.recipes} fallback={<RecipeEmptyState />}>
            {(item) => (
              <button
                type="button"
                class={`rounded-md border p-3 text-left transition-all ${
                  props.selectedRecipeID === item.id
                    ? "border-[var(--oa-plugin-accent,#6366f1)] bg-[color-mix(in_srgb,var(--oa-plugin-accent,#6366f1)_8%,transparent)] shadow-sm"
                    : "border-border-weak-base bg-background-base hover:shadow-sm"
                } ${item.disabledReason ? "opacity-70" : ""}`}
                onClick={() => props.onSelectRecipe(item)}
              >
                <div class="flex items-start justify-between gap-2">
                  <div class="min-w-0">
                    <div class="truncate text-14-medium text-text-strong">{item.title}</div>
                    <div class="mt-1 line-clamp-2 text-12-regular text-text-base">{item.description}</div>
                  </div>
                  <span class="shrink-0 rounded-full border border-border-weak-base px-2 py-0.5 text-11-regular text-text-base">
                    {item.sourceLabel ?? item.source}
                  </span>
                </div>
                <Show when={item.disabledReason}>
                  <div class="mt-2 text-11-regular text-text-danger">{item.disabledReason}</div>
                </Show>
              </button>
            )}
          </For>
        </div>
      </div>

      <Show when={props.recipeImportOpen}>
        <div class="grid grid-cols-1 gap-4 rounded-lg border border-border-weak-base bg-background-secondary p-4 lg:grid-cols-2">
          <label class="flex flex-col gap-2 text-12-medium text-text-base">
            <span>Recipe JSON</span>
            <textarea
              value={props.recipeJson}
              onInput={(event) => props.onRecipeJson(event.currentTarget.value)}
              placeholder='{"id":"workspace-recipe","title":"Workspace Recipe","scenarios":[{"id":"default","title":"Default","prompt":"Create an ad"}]}'
              class="min-h-28 rounded-md border border-border-weak-base bg-background-base px-3 py-2 text-12-regular text-text-strong"
            />
            <Button type="button" variant="secondary" onClick={props.onImportJson}>
              Import JSON
            </Button>
          </label>
          <label class="flex flex-col gap-2 text-12-medium text-text-base">
            <span>10x public scene pack URL</span>
            <input
              value={props.publicPackUrl}
              onInput={(event) => props.onPublicPackUrl(event.currentTarget.value)}
              placeholder="Public pack URL or handle/scene-pack"
              class="rounded-md border border-border-weak-base bg-background-base px-3 py-2 text-13-regular text-text-strong"
            />
            <Button type="button" variant="secondary" onClick={props.onSyncPublicPack}>
              Import 10x Pack
            </Button>
          </label>
        </div>
      </Show>

      <div class="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
        <div class="flex min-w-0 flex-col gap-4">
          <form
            ref={(el) => {
              form = el
            }}
            class="rounded-lg border border-border-weak-base bg-background-secondary p-4"
            on:submit={(event) => {
              event.preventDefault()
              if (form) void props.onRun(form)
            }}
          >
            <div class="mb-4 flex items-start justify-between gap-3">
              <div class="min-w-0">
                <h3 class="text-15-medium text-text-strong">{recipe()?.title ?? "Workflow"}</h3>
                <div class="mt-1 line-clamp-2 text-13-regular text-text-base">{recipe()?.description}</div>
              </div>
              <Show when={recipe()?.sourceLabel ?? recipe()?.source}>
                {(source) => (
                  <span class="shrink-0 rounded-full border border-border-weak-base bg-background-base px-2.5 py-0.5 text-11-regular text-text-base">
                    {source()}
                  </span>
                )}
              </Show>
            </div>

            <div class="grid grid-cols-1 gap-3 md:grid-cols-2">
              <label class="flex flex-col gap-1 text-13-medium text-text-base">
                Scenario
                <select
                  name="workflow_scenario"
                  value={props.selectedScenarioID}
                  class="rounded-md border border-border-weak-base bg-background-base px-3 py-2 text-14-regular text-text-strong"
                  onChange={(event) => props.onSelectScenario(event.currentTarget.value)}
                >
                  <For each={recipe()?.scenarios ?? []}>{(item) => <option value={item.id}>{item.title}</option>}</For>
                </select>
              </label>
              <label class="flex flex-col gap-1 text-13-medium text-text-base">
                Aspect
                <select
                  name="workflow_aspect_ratio"
                  value={recipe()?.defaultAspectRatio ?? "1:1"}
                  class="rounded-md border border-border-weak-base bg-background-base px-3 py-2 text-14-regular text-text-strong"
                >
                  <For each={recipe()?.aspectRatios ?? ["1:1", "16:9", "9:16"]}>{(item) => <option value={item}>{item}</option>}</For>
                </select>
              </label>
            </div>
            <label class="mt-3 flex flex-col gap-2 text-12-medium text-text-base">
              <span>Prompt / style direction</span>
              <textarea
                name="workflow_prompt"
                required
                placeholder="Describe the offer, audience, channel, or product angle…"
                class="min-h-28 rounded-md border border-border-weak-base bg-background-base px-3 py-2 text-14-regular text-text-strong"
              />
            </label>
            <div class="mt-3">
              <GenerativeField
                input={{
                  key: "workflow_variants",
                  label: "Variants",
                  type: "number",
                  min: minVariants(),
                  max: maxVariants(),
                  step: 1,
                  default: defaultVariants(),
                }}
              />
            </div>
            <RecipeTemplateSummary recipe={recipe()} />
            <Show when={props.disableReason}>
              <div class="mt-3 rounded-md border border-border-weak-base bg-background-base px-3 py-2 text-12-regular text-text-base">
                {props.disableReason}
              </div>
            </Show>
            <div class="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center">
              <Button type="submit" variant="primary" size="large" disabled={!props.canGenerate || props.pending || !scenario()}>
                Run Workflow
              </Button>
              <Show when={latestRun()}>
                {(run) => <span class="text-12-regular text-text-base">Latest run · {run().jobIDs?.length ?? 0} variants</span>}
              </Show>
            </div>
          </form>
        </div>
        <WorkflowResultPanel
          run={latestRun()}
          savedJobIDs={props.savedJobIDs}
          commands={props.commands}
          onSubmit={props.onSubmit}
          onOpen={props.onOpen}
        />
      </div>

      <RecipeRunQueue
        runs={props.runs}
        savedJobIDs={props.savedJobIDs}
        commands={props.commands}
        onSubmit={props.onSubmit}
        onOpen={props.onOpen}
      />
      <RecipeRunGallery
        runs={props.completedRuns}
        commands={props.commands}
        onSubmit={props.onSubmit}
        onOpen={props.onOpen}
      />
      </Show>
    </>
  )
}

function PipelineDesigner(props: {
  pipelines?: GenerativePipelines
  commands: Record<string, string>
  pending: boolean
  readonly?: boolean
  onSubmit: CommandSubmit
}) {
  const [selectedID, setSelectedID] = createSignal(props.pipelines?.designs[0]?.id ?? "")
  const designs = () => props.pipelines?.designs ?? []
  const selected = createMemo(() => designs().find((design) => design.id === selectedID()) ?? designs()[0])

  createEffect(() => {
    if (selected() || !designs()[0]) return
    setSelectedID(designs()[0].id)
  })

  function command(name: "save" | "export_markdown" | "export_recipe" | "delete") {
    return props.commands[name] ?? props.commands[name.replaceAll("_", ".")] ?? `pipeline.${name.replaceAll("_", ".")}`
  }

  function submitPipeline(_blockId: string, values: Record<string, unknown>) {
    const nextCommand = String(values.command ?? "")
    const pipelineId = String(values.pipelineId ?? selected()?.id ?? "")
    if (nextCommand === "pipeline.save" || nextCommand === command("save")) {
      return props.onSubmit(nextCommand, {
        pipelineId,
        title: selected()?.title ?? "Untitled Pipeline",
        graph: values.graph,
      })
    }
    return props.onSubmit(nextCommand, { pipelineId })
  }

  function createSamplePipeline() {
    return props.onSubmit(command("save"), {
      title: "Sample Ad Campaign Pipeline",
      graph: sampleAdCampaignPipelineGraph(),
    })
  }

  return (
    <div class="flex flex-col gap-4">
      <div class="rounded-lg border border-border-weak-base bg-background-secondary p-4">
        <div class="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 class="text-15-medium text-text-strong">Pipeline Designer</h3>
            <div class="mt-1 text-12-regular text-text-base">{designs().length} pipeline designs</div>
          </div>
          <Button type="button" variant="secondary" disabled={props.readonly || props.pending} onClick={createSamplePipeline}>
            New Pipeline
          </Button>
        </div>
        <div class="mt-3 flex flex-wrap gap-2">
          <For each={designs()} fallback={<div class="text-13-regular text-text-base">No pipeline designs yet.</div>}>
            {(design) => (
              <button
                type="button"
                class={`rounded-md border px-3 py-2 text-left text-13-medium ${
                  selected()?.id === design.id ? "border-border-strong bg-background-base text-text-strong" : "border-border-weak-base bg-background-base text-text-base"
                }`}
                onClick={() => setSelectedID(design.id)}
              >
                {design.title}
              </button>
            )}
          </For>
        </div>
      </div>
      <Show when={selected()}>
        {(design) => (
          <div class="rounded-lg border border-border-weak-base bg-background-secondary p-4">
            <div class="mb-3 flex flex-wrap items-center justify-between gap-3">
              <div class="min-w-0">
                <h3 class="truncate text-15-medium text-text-strong">{design().title}</h3>
                <Show when={design().staleExport}>
                  <div class="mt-1 text-12-regular text-text-base">Recipe export is stale</div>
                </Show>
              </div>
              <Show when={design().lastExportedRecipeId}>
                {(recipeId) => (
                  <span class="rounded-full border border-border-weak-base bg-background-base px-2 py-0.5 text-11-regular text-text-base">
                    {recipeId()}
                  </span>
                )}
              </Show>
            </div>
            <VisualPipelineBlockView
              block={{
                id: design().id,
                type: "visual_pipeline",
                title: design().title,
                mode: "design",
                submitToolName: "generative.command",
                graph: design().graph,
                nodeTypes: props.pipelines?.nodeTypes ?? [],
                validation: design().validation,
                exportActions: [
                  { command: command("export_markdown") as "pipeline.export_markdown", label: "Export Markdown" },
                  { command: command("export_recipe") as "pipeline.export_recipe", label: "Export Recipe" },
                ],
              }}
              pending={props.pending}
              readonly={props.readonly}
              onSubmit={submitPipeline}
            />
          </div>
        )}
      </Show>
    </div>
  )
}

function sampleAdCampaignPipelineGraph(): VisualPipelineGraph {
  return {
    schemaVersion: 1,
    nodes: [
      {
        id: "brief",
        type: "brief",
        label: "Brief",
        position: { x: 0, y: 0 },
        data: { prompt: "Create a paid social launch campaign for a new product or offer." },
      },
      {
        id: "audience",
        type: "audience",
        label: "Audience",
        position: { x: 240, y: 0 },
        data: { audience: "High-intent buyers researching the category." },
      },
      {
        id: "offer",
        type: "offer",
        label: "Offer",
        position: { x: 480, y: 0 },
        data: { offer: "Lead with a clear launch benefit and reason to act now." },
      },
      {
        id: "asset",
        type: "asset_slot",
        label: "Asset",
        position: { x: 720, y: 0 },
        data: { kind: "image" },
      },
      {
        id: "scene",
        type: "scene",
        label: "Scene",
        position: { x: 960, y: 0 },
        data: { prompt: "Show the product in a clean, brand-safe paid social composition." },
      },
      {
        id: "variants",
        type: "variant_set",
        label: "Variants",
        position: { x: 1200, y: 0 },
        data: { count: 3 },
      },
      {
        id: "output",
        type: "output_pack",
        label: "Output",
        position: { x: 1440, y: 0 },
        data: { format: "image", aspectRatio: "1:1" },
      },
      {
        id: "review",
        type: "review_gate",
        label: "Review",
        position: { x: 1680, y: 0 },
        data: { criteria: "Check brand fit, product clarity, and claim safety." },
      },
    ],
    edges: [
      { id: "edge-brief-audience", from: "brief", to: "audience" },
      { id: "edge-audience-offer", from: "audience", to: "offer" },
      { id: "edge-offer-asset", from: "offer", to: "asset" },
      { id: "edge-asset-scene", from: "asset", to: "scene" },
      { id: "edge-scene-variants", from: "scene", to: "variants" },
      { id: "edge-variants-output", from: "variants", to: "output" },
      { id: "edge-output-review", from: "output", to: "review" },
    ],
  }
}

function RecipeTemplateSummary(props: { recipe?: GenerativeRecipe }) {
  const fields = () => props.recipe?.template?.fields ?? []
  return (
    <Show when={fields().length > 0}>
      <div class="mt-4 rounded-md border border-border-weak-base bg-background-base p-3">
        <div class="flex items-center justify-between gap-3">
          <div class="min-w-0">
            <div class="text-12-medium text-text-strong">{props.recipe?.template?.title}</div>
            <div class="mt-0.5 line-clamp-1 text-11-regular text-text-base">{props.recipe?.template?.description}</div>
          </div>
          <span class="shrink-0 rounded-full border border-border-weak-base px-2 py-0.5 text-11-regular text-text-base">
            {(props.recipe?.template?.outputs ?? ["image"]).join(", ")}
          </span>
        </div>
        <div class="mt-3 flex flex-wrap gap-1.5">
          <For each={fields()}>
            {(field) => (
              <span class="rounded-md border border-border-weak-base bg-background-secondary px-2 py-1 text-11-regular text-text-base">
                {field.key}
                <Show when={field.default !== undefined}>
                  <span class="text-text-base"> · {String(field.default)}</span>
                </Show>
              </span>
            )}
          </For>
        </div>
      </div>
    </Show>
  )
}

function WorkflowResultPanel(props: {
  run?: RecipeRun
  savedJobIDs: Set<string>
  commands: Record<string, string>
  onSubmit: CommandSubmit
  onOpen: (job: Record<string, unknown>) => void
}) {
  const jobs = () => props.run?.jobs ?? []
  const completed = () => jobs().filter((job) => String(job.status) === "completed" && outputUrl(job))
  const failed = () => jobs().filter((job) => ["failed", "expired"].includes(String(job.status)))
  const running = () => jobs().filter((job) => RUNNING.includes(String(job.status)))
  const duration = () => {
    const started = Number(props.run?.createdAt)
    const finished = Number(props.run?.completedAt ?? props.run?.updatedAt)
    if (!Number.isFinite(started) || !Number.isFinite(finished) || finished <= started) return ""
    return `${((finished - started) / 1000).toFixed(1)}s`
  }
  return (
    <div class="min-w-0 rounded-lg border border-border-weak-base bg-background-secondary p-4">
      <div class="mb-3 flex items-center justify-between gap-3">
        <div>
          <h3 class="text-15-medium text-text-strong">Result</h3>
          <div class="mt-1 text-12-regular text-text-base">
            <Show when={props.run} fallback="Generations appear here">
              {(run) => (
                <>
                  {run().title} · {jobs().length} calls
                </>
              )}
            </Show>
          </div>
        </div>
        <Show when={props.run}>
          {(run) => <StatusChip status={run().status} />}
        </Show>
      </div>

      <Show
        when={props.run}
        fallback={
          <div class="flex min-h-64 flex-col items-center justify-center rounded-lg border border-dashed border-border-weak-base bg-background-base p-8 text-center">
            <div class="text-11-medium uppercase tracking-wider text-text-base">output preview</div>
            <div class="mt-3 text-15-medium text-text-strong">Generations appear here</div>
            <div class="mt-1 text-13-regular text-text-base">Four takes, one click.</div>
          </div>
        }
      >
        <div class="mb-3 flex flex-wrap gap-2">
          <span class="rounded-full border border-border-weak-base bg-background-base px-2.5 py-1 text-12-regular text-text-base">
            <span class="text-text-strong">{jobs().length}</span> calls
          </span>
          <span class="rounded-full border border-border-weak-base bg-background-base px-2.5 py-1 text-12-regular text-text-base">
            <span class="text-text-strong">{completed().length}</span> ok
          </span>
          <span class="rounded-full border border-border-weak-base bg-background-base px-2.5 py-1 text-12-regular text-text-base">
            <span class="text-text-strong">{failed().length}</span> failed
          </span>
          <Show when={duration()}>
            {(value) => (
              <span class="rounded-full border border-border-weak-base bg-background-base px-2.5 py-1 text-12-regular text-text-base">
                {value()}
              </span>
            )}
          </Show>
        </div>

        <div class="grid min-h-64 grid-cols-1 gap-3 md:grid-cols-2">
          <For each={jobs()} fallback={<GalleryEmptyState />}>
            {(job) => (
              <div class="overflow-hidden rounded-md border border-border-weak-base bg-background-base">
                <div class="flex items-center justify-between gap-2 px-3 py-2">
                  <span class="truncate text-12-medium text-text-strong">
                    {formatParam((job.params as Record<string, unknown> | undefined)?.variantLabel ?? job.id)}
                  </span>
                  <StatusChip status={String(job.status)} />
                </div>
                <Show
                  when={outputUrl(job)}
                  fallback={
                    <div class={`w-full overflow-hidden bg-background-secondary ${aspectClass(job)}`}>
                      <div
                        class="h-full w-full bg-background-base"
                        classList={{ "animate-pulse": RUNNING.includes(String(job.status)) }}
                      />
                    </div>
                  }
                >
                  <button
                    type="button"
                    class="block w-full text-left"
                    onClick={() => props.onOpen(job)}
                  >
                    <OutputPreview job={job} compact commands={props.commands} onSubmit={props.onSubmit} />
                  </button>
                  <div class="p-2">
                    <JobActions
                      job={job}
                      saved={props.savedJobIDs.has(String(job.id))}
                      commands={props.commands}
                      onSubmit={props.onSubmit}
                      onOpen={props.onOpen}
                    />
                  </div>
                </Show>
                <Show when={job.error}>
                  <div class="px-3 pb-3">
                    <ErrorBlock error={job.error} />
                  </div>
                </Show>
              </div>
            )}
          </For>
        </div>

        <Show when={running().length > 0}>
          <div class="mt-3 flex justify-end">
            <Button
              type="button"
              variant="secondary"
              onClick={() =>
                running().forEach((job) => props.onSubmit(props.commands.generation_poll, {}, { jobID: String(job.id) }))
              }
            >
              Poll Running
            </Button>
          </div>
        </Show>
      </Show>
    </div>
  )
}

function RecipeRunQueue(props: {
  runs: RecipeRun[]
  savedJobIDs: Set<string>
  commands: Record<string, string>
  onSubmit: CommandSubmit
  onOpen: (job: Record<string, unknown>) => void
}) {
  return (
    <div class="rounded-lg border border-border-weak-base bg-background-secondary p-4">
      <div class="mb-3 flex items-center justify-between gap-3">
        <h3 class="text-15-medium text-text-strong">Workflow Queue</h3>
        <span class="text-12-regular text-text-base">{props.runs.length} runs</span>
      </div>
      <div class="flex flex-col gap-2">
        <For each={props.runs} fallback={<QueueEmptyState />}>
          {(run) => (
            <div class="rounded-md border border-border-weak-base bg-background-base p-3">
              <div class="flex items-start justify-between gap-3">
                <div class="min-w-0">
                  <div class="truncate text-14-medium text-text-strong">{run.title}</div>
                  <div class="mt-1 flex items-center gap-2">
                    <StatusChip status={run.status} />
                    <span class="text-12-regular text-text-base">{run.jobIDs?.length ?? 0} outputs</span>
                  </div>
                </div>
                <Show when={(run.jobs ?? []).some((job) => RUNNING.includes(String(job.status)))}>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() =>
                      (run.jobs ?? [])
                        .filter((job) => RUNNING.includes(String(job.status)))
                        .forEach((job) => props.onSubmit(props.commands.generation_poll, {}, { jobID: String(job.id) }))
                    }
                  >
                    Poll
                  </Button>
                </Show>
              </div>
              <div class="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                <For each={run.jobs ?? []}>
                  {(job) => (
                    <div class="rounded-md border border-border-weak-base bg-background-secondary p-2">
                      <div class="mb-2 flex items-center justify-between gap-2">
                        <span class="truncate text-12-medium text-text-strong">
                          {formatParam((job.params as Record<string, unknown> | undefined)?.variantLabel ?? job.id)}
                        </span>
                        <StatusChip status={String(job.status)} />
                      </div>
                      <Show
                        when={outputUrl(job)}
                        fallback={
                          <div class={`w-full overflow-hidden rounded-md bg-background-base ${aspectClass(job)}`}>
                            <div class="h-full w-full animate-pulse bg-background-secondary" />
                          </div>
                        }
                      >
                        <OutputPreview job={job} compact commands={props.commands} onSubmit={props.onSubmit} />
                        <div class="mt-2">
                          <JobActions
                            job={job}
                            saved={props.savedJobIDs.has(String(job.id))}
                            commands={props.commands}
                            onSubmit={props.onSubmit}
                            onOpen={props.onOpen}
                          />
                        </div>
                      </Show>
                    </div>
                  )}
                </For>
              </div>
              <Show when={run.error}>
                <ErrorBlock error={run.error} />
              </Show>
            </div>
          )}
        </For>
      </div>
    </div>
  )
}

function RecipeRunGallery(props: {
  runs: RecipeRun[]
  commands: Record<string, string>
  onSubmit: CommandSubmit
  onOpen: (job: Record<string, unknown>) => void
}) {
  const jobs = () => props.runs.flatMap((run) => run.jobs ?? []).filter((job) => String(job.status) === "completed" && outputUrl(job))
  return <GenerationGallery jobs={jobs()} commands={props.commands} onSubmit={props.onSubmit} onOpen={props.onOpen} />
}

function RecipeEmptyState() {
  return (
    <div class="rounded-md border border-dashed border-border-weak-base bg-background-base px-4 py-8 text-center text-12-regular text-text-base md:col-span-3">
      No workflow recipes loaded.
    </div>
  )
}

function StatusChip(props: { status: string }) {
  const variant = () => {
    const s = props.status.toLowerCase()
    if (s === "completed")
      return { text: "text-text-strong", dot: "bg-icon-success-active" }
    if (s === "failed" || s === "expired")
      return { text: "text-text-danger", dot: "bg-icon-critical-base" }
    if (RUNNING.includes(s))
      return { text: "text-text-base", dot: "bg-icon-warning-active animate-pulse" }
    return { text: "text-text-base", dot: "bg-icon-weak-base" }
  }
  return (
    <span
      class={`inline-flex items-center gap-1.5 rounded-full border border-border-weak-base bg-background-secondary px-2 py-0.5 text-11-medium ${variant().text}`}
    >
      <span class={`inline-block h-1.5 w-1.5 rounded-full ${variant().dot}`} />
      {props.status}
    </span>
  )
}

function OutputPreview(props: {
  job: Record<string, unknown>
  compact?: boolean
  commands?: Record<string, string>
  onSubmit?: CommandSubmit
}) {
  const [failedUrl, setFailedUrl] = createSignal("")
  const [autoRefreshUrl, setAutoRefreshUrl] = createSignal("")
  const url = () => outputUrl(props.job)
  const failed = () => failedUrl() === url()

  createEffect(() => {
    const current = url()
    if (current && failedUrl() && failedUrl() !== current) setFailedUrl("")
  })

  function refresh(force = false) {
    const current = url()
    const command = props.commands?.generation_poll
    if (!current || !command || !props.onSubmit) return
    if (!force && autoRefreshUrl() === current) return
    setAutoRefreshUrl(current)
    void props.onSubmit(command, { refresh: true }, { jobID: String(props.job.id) })
  }

  function handleError() {
    const current = url()
    if (!current) return
    setFailedUrl(current)
  }

  return (
    <Show when={url()}>
      {(url) => (
        <Show
          when={!failed()}
          fallback={
            <div
              class={`${props.compact ? "h-40" : "min-h-48 max-h-80"} flex w-full flex-col items-center justify-center gap-2 rounded-md border border-border-weak-base bg-background-secondary p-4 text-center`}
            >
              <div class="text-13-medium text-text-strong">Preview unavailable</div>
              <div class="text-12-regular text-text-base">The output link may have expired.</div>
              <Show when={!props.compact && props.commands?.generation_poll && props.onSubmit}>
                <Button type="button" variant="secondary" onClick={() => refresh(true)}>
                  Refresh preview
                </Button>
              </Show>
            </div>
          }
        >
          <Show
            when={outputKind(props.job) === "video"}
            fallback={
              <img
                src={url()}
                alt={String(props.job.prompt ?? "Generated output")}
                class={`${props.compact ? "h-40" : "max-h-80"} w-full rounded-md bg-background-secondary object-contain`}
                onError={handleError}
              />
            }
          >
            <video
              src={url()}
              controls={!props.compact}
              muted={props.compact}
              class={`${props.compact ? "h-40" : "max-h-80"} w-full rounded-md bg-background-secondary object-contain`}
              onError={handleError}
            />
          </Show>
        </Show>
      )}
    </Show>
  )
}

// A single active-reference thumbnail (uploaded source OR a library pick) with a
// hover Remove. Non-images show a labeled tile.
function ReferenceThumb(props: { asset: Record<string, unknown>; onRemove: () => void }): JSX.Element {
  const preview = String(props.asset.previewUrl ?? props.asset.remoteUrl ?? "")
  const isImage = mimeKind(String(props.asset.mime ?? "")) === "image"
  return (
    <div
      class="group relative aspect-square overflow-hidden rounded-lg border border-border-weak-base bg-background-base"
      title={String(props.asset.filename)}
    >
      <Show
        when={isImage && preview}
        fallback={
          <div class="flex h-full w-full flex-col items-center justify-center gap-1 p-2 text-center">
            <span class="text-10-medium uppercase tracking-wide text-text-base">
              {mimeKind(String(props.asset.mime ?? "")) || "file"}
            </span>
            <span class="line-clamp-2 w-full text-10-regular text-text-base">{String(props.asset.filename)}</span>
          </div>
        }
      >
        <img src={preview} alt={String(props.asset.filename)} loading="lazy" class="h-full w-full object-cover" />
      </Show>
      <button
        type="button"
        data-generative-reference-remove={String(props.asset.id ?? "")}
        title="Remove reference"
        onClick={() => props.onRemove()}
        class="absolute right-1 top-1 flex size-5 items-center justify-center rounded-full bg-black/55 text-white opacity-0 backdrop-blur-sm transition-opacity hover:bg-black/80 focus:opacity-100 group-hover:opacity-100"
      >
        <Icon name="close-small" size="small" />
      </button>
    </div>
  )
}

function GenerativeField(props: { input: GenerativeInput }) {
  const base =
    "w-full rounded-md border border-border-weak-base bg-background-base px-3 py-2 text-14-regular text-text-strong"
  const isSmallInteger = () =>
    props.input.type === "number" &&
    props.input.min !== undefined &&
    props.input.max !== undefined &&
    props.input.max - props.input.min <= 8 &&
    (props.input.step ?? 1) === 1
  return (
    <label class="flex w-full flex-col gap-1 text-13-medium text-text-base">
      <Show when={props.input.type !== "toggle"}>{props.input.label}</Show>
      {props.input.type === "textarea" ? (
        <textarea
          name={props.input.key}
          required={props.input.required}
          value={String(props.input.default ?? "")}
          class={`${base} min-h-24 resize-y`}
        />
      ) : props.input.type === "select" ? (
        <select
          name={props.input.key}
          class={base}
          required={props.input.required}
          value={String(props.input.default ?? "")}
        >
          <For each={props.input.options ?? []}>{(option) => <option value={option}>{option}</option>}</For>
        </select>
      ) : props.input.type === "toggle" ? (
        <span class="flex items-center gap-2">
          <input name={props.input.key} type="checkbox" checked={Boolean(props.input.default)} />
          <span class="text-13-regular text-text-base">{props.input.label}</span>
        </span>
      ) : props.input.type === "range" ? (
        <RangeField input={props.input} />
      ) : isSmallInteger() ? (
        <NumberStepper input={props.input} />
      ) : (
        <input
          name={props.input.key}
          type={props.input.type === "number" ? "number" : "text"}
          min={props.input.min}
          max={props.input.max}
          step={props.input.step}
          value={props.input.default === undefined ? "" : String(props.input.default)}
          required={props.input.required}
          class={base}
        />
      )}
    </label>
  )
}

function NumberStepper(props: { input: GenerativeInput }) {
  const min = props.input.min ?? 1
  const presetMax = props.input.max ?? 4
  const hardMax = 24 // generation fans out N calls server-side; keep a sane ceiling
  const presets = Array.from({ length: presetMax - min + 1 }, (_, i) => min + i)
  const [value, setValue] = createSignal(Number(props.input.default ?? min))
  const [custom, setCustom] = createSignal(false)
  // The accent fill used for the active cell, matching the studio tabs.
  const accent = {
    color: "var(--oa-plugin-accent, #4f46e5)",
    background: "color-mix(in srgb, var(--oa-plugin-accent, #6366f1) 14%, transparent)",
  }
  const clamp = (n: number) => Math.max(min, Math.min(hardMax, Number.isFinite(n) ? Math.round(n) : min))
  const persist = (event: Event) => {
    const form = event.currentTarget instanceof Element ? event.currentTarget.closest("form") : null
    queueMicrotask(() => form?.dispatchEvent(new Event("change", { bubbles: true })))
  }
  // The segmented control fills the whole field width (flex-1 cells), so there is
  // no dead space to the right; a "Custom" cell reveals a free number input.
  return (
    <div class="flex w-full items-stretch gap-2">
      <div class="flex w-full overflow-hidden rounded-md border border-border-weak-base">
        <For each={presets}>
          {(n) => {
            const active = () => !custom() && value() === n
            return (
              <button
                type="button"
                aria-pressed={active()}
                data-selected={active() ? "true" : "false"}
                class={`flex-1 px-3 py-1.5 text-13-medium transition-all ${
                  active() ? "font-semibold" : "text-text-base hover:text-text-strong"
                }`}
                style={active() ? accent : undefined}
                onClick={(event) => {
                  setCustom(false)
                  setValue(n)
                  persist(event)
                }}
              >
                {n}
              </button>
            )
          }}
        </For>
        <button
          type="button"
          aria-pressed={custom()}
          class={`flex-1 border-l border-border-weak-base px-3 py-1.5 text-13-medium transition-all ${
            custom() ? "font-semibold" : "text-text-base hover:text-text-strong"
          }`}
          style={custom() ? accent : undefined}
          onClick={(event) => {
            setCustom(true)
            if (presets.includes(value())) setValue(clamp(presetMax + 1))
            persist(event)
          }}
        >
          Custom
        </button>
      </div>
      <Show when={custom()}>
        <input
          type="number"
          min={min}
          max={hardMax}
          value={String(value())}
          aria-label="Custom count"
          class="w-24 shrink-0 rounded-md border border-border-weak-base bg-background-base px-3 py-1.5 text-13-medium text-text-strong"
          onInput={(event) => {
            setValue(clamp(Number(event.currentTarget.value)))
            persist(event)
          }}
        />
      </Show>
      <input type="hidden" name={props.input.key} value={String(value())} />
    </div>
  )
}

function RangeField(props: { input: GenerativeInput }) {
  const min = props.input.min ?? 0
  const max = props.input.max ?? 1
  const step = props.input.step ?? 0.1
  const [value, setValue] = createSignal(Number(props.input.default ?? min))
  const persist = (event: Event) => {
    const form = event.currentTarget instanceof Element ? event.currentTarget.closest("form") : null
    queueMicrotask(() => form?.dispatchEvent(new Event("change", { bubbles: true })))
  }

  return (
    <div class="rounded-md border border-border-weak-base bg-background-base px-3 py-2">
      <div class="mb-2 flex items-center justify-between gap-3">
        <span class="text-12-regular text-text-base">{min}</span>
        <span class="rounded-md border border-border-weak-base px-2 py-0.5 text-12-medium text-text-strong">
          {value()}
        </span>
        <span class="text-12-regular text-text-base">{max}</span>
      </div>
      <input
        name={props.input.key}
        type="range"
        min={min}
        max={max}
        step={step}
        value={String(value())}
        class="w-full accent-[var(--oa-plugin-accent,#6366f1)]"
        onInput={(event) => {
          setValue(Number(event.currentTarget.value))
          persist(event)
        }}
      />
    </div>
  )
}

function BrandDNAWorkspace(props: {
  brandDNA?: BrandDNAState
  campaigns?: CampaignState
  commands: Record<string, string>
  onSubmit: CommandSubmit
}) {
  const [brandURL, setBrandURL] = createSignal("")
  const [campaignGoal, setCampaignGoal] = createSignal("")
  const profiles = () => props.brandDNA?.profiles ?? []
  const activeProfile = createMemo(
    () => profiles().find((profile) => profile.id === props.brandDNA?.activeID) ?? profiles()[0],
  )
  const latestVersion = createMemo(() => activeProfile()?.versions?.[0])
  const brandCommand = (name: "analyze_url" | "sync_design_file" | "restore_version") =>
    props.brandDNA?.commands?.[name] ?? props.commands[`brand_${name}`]
  const campaignCommand = (name: "create_from_brand" | "export" | "delete") =>
    props.campaigns?.commands?.[name] ?? props.commands[`campaign_${name}`]

  function analyzeBrandURL() {
    const command = brandCommand("analyze_url")
    const url = brandURL().trim()
    if (!command || !url) return
    return props.onSubmit(command, { url })
  }

  function syncDesignFile(profile: BrandDNAProfile) {
    const command = brandCommand("sync_design_file")
    if (!command) return
    return props.onSubmit(command, { brandDNAID: profile.id })
  }

  function restoreVersion(profile: BrandDNAProfile, version: BrandDNAVersion) {
    const command = brandCommand("restore_version")
    if (!command) return
    if (profile.syncStatus === "unsynced" && !window.confirm("Replace unsynced DESIGN.md edits with this Brand DNA version?")) return
    return props.onSubmit(command, {
      brandDNAID: profile.id,
      versionID: version.id,
      ...(profile.syncStatus === "unsynced" ? { overwrite: true } : {}),
    })
  }

  function createCampaignBrief() {
    const profile = activeProfile()
    const command = campaignCommand("create_from_brand")
    const goal = campaignGoal().trim()
    if (!profile || !command || !goal) return
    return props.onSubmit(command, {
      brandDNAID: profile.id,
      goal,
      platforms: ["linkedin"],
      language: "English",
    })
  }

  return (
    <div class="flex flex-col gap-4">
      <div class="rounded-lg border border-border-weak-base bg-background-secondary p-4">
        <div class="mb-3 flex items-center justify-between gap-3">
          <h3 class="text-15-medium text-text-strong">Brand DNA</h3>
          <Show when={activeProfile()?.versionNo ?? latestVersion()?.versionNo}>
            {(version) => (
              <span class="rounded-full border border-border-weak-base bg-background-base px-2 py-0.5 text-11-regular text-text-base">
                v{version()}
              </span>
            )}
          </Show>
        </div>
        <Show
          when={activeProfile()}
          fallback={<div class="text-13-regular text-text-base">Analyze a brand URL to create Brand DNA.</div>}
        >
          {(profile) => (
            <div class="flex flex-col gap-3">
              <div class="rounded-md border border-border-weak-base bg-background-base p-3">
                <div class="text-14-medium text-text-strong">{profile().name}</div>
                <div class="mt-1 text-12-regular text-text-base">{profile().sourceUrl}</div>
                <div class="mt-2 flex flex-wrap gap-1 text-11-regular text-text-base">
                  <Show when={profile().designPath}>
                    {(path) => <span class="rounded-full border border-border-weak-base px-2 py-0.5">{path()}</span>}
                  </Show>
                  <Show when={profile().syncStatus}>
                    {(status) => <span class="rounded-full border border-border-weak-base px-2 py-0.5">{status()}</span>}
                  </Show>
                </div>
                <Show when={profile().colors?.length}>
                  <div class="mt-2 text-11-regular text-text-base">Colors: {profile().colors?.join(", ")}</div>
                </Show>
                <Show when={profile().fonts?.length}>
                  <div class="mt-1 text-11-regular text-text-base">Fonts: {profile().fonts?.join(", ")}</div>
                </Show>
                <Show when={profile().syncWarnings?.length}>
                  <div class="mt-2 flex flex-col gap-1">
                    <For each={profile().syncWarnings}>
                      {(warning) => <div class="text-12-regular text-text-base">{warning}</div>}
                    </For>
                  </div>
                </Show>
              </div>
              <Show when={latestVersion()}>
                {(version) => (
                  <div class="rounded-md border border-border-weak-base bg-background-base p-3">
                    <div class="flex flex-wrap items-center justify-between gap-2">
                      <div class="min-w-0">
                        <div class="text-13-medium text-text-strong">
                          v{version().versionNo ?? profile().versionNo ?? 1}
                        </div>
                        <div class="mt-1 text-12-regular text-text-base">{version().designPath ?? profile().designPath}</div>
                      </div>
                      <div class="flex flex-wrap gap-2">
                        <Show when={brandCommand("sync_design_file")}>
                          <Button type="button" variant="secondary" onClick={() => syncDesignFile(profile())}>
                            Sync DESIGN.md
                          </Button>
                        </Show>
                        <Show when={brandCommand("restore_version")}>
                          <Button type="button" variant="secondary" onClick={() => restoreVersion(profile(), version())}>
                            Restore version
                          </Button>
                        </Show>
                      </div>
                    </div>
                  </div>
                )}
              </Show>
            </div>
          )}
        </Show>
        <label class="mt-4 flex flex-col gap-2 text-12-medium text-text-base">
          <span>Brand URL</span>
          <input
            name="brand_url"
            value={brandURL()}
            onInput={(event) => setBrandURL(event.currentTarget.value)}
            placeholder="https://brand.example"
            class="rounded-md border border-border-weak-base bg-background-base px-3 py-2 text-13-regular text-text-strong"
          />
        </label>
        <div class="mt-3">
          <Button type="button" variant="secondary" disabled={!brandCommand("analyze_url")} onClick={analyzeBrandURL}>
            Analyze URL
          </Button>
        </div>
      </div>

      <div class="rounded-lg border border-border-weak-base bg-background-secondary p-4">
        <h3 class="text-15-medium text-text-strong">Campaign Briefs</h3>
        <label class="mt-3 flex flex-col gap-2 text-12-medium text-text-base">
          <span>Goal</span>
          <input
            name="campaign_goal"
            value={campaignGoal()}
            onInput={(event) => setCampaignGoal(event.currentTarget.value)}
            placeholder="Launch summer kit"
            class="rounded-md border border-border-weak-base bg-background-base px-3 py-2 text-13-regular text-text-strong"
          />
        </label>
        <div class="mt-3">
          <Button
            type="button"
            variant="secondary"
            disabled={!campaignCommand("create_from_brand") || !activeProfile()}
            onClick={createCampaignBrief}
          >
            Create campaign brief
          </Button>
        </div>
        <div class="mt-4 flex flex-col gap-2">
          <For each={props.campaigns?.plans ?? []} fallback={<div class="text-12-regular text-text-base">No campaign briefs yet.</div>}>
            {(plan) => (
              <div class="rounded-md border border-border-weak-base bg-background-base p-3">
                <div class="text-13-medium text-text-strong">{plan.title}</div>
                <div class="mt-1 text-12-regular text-text-base">
                  {[plan.status, plan.language, ...(plan.platforms ?? [])].filter(Boolean).join(" · ")}
                </div>
                <div class="mt-3 flex flex-wrap gap-2">
                  <Show when={campaignCommand("export")}>
                    {(command) => (
                      <Button type="button" variant="secondary" onClick={() => props.onSubmit(command(), { campaignID: plan.id })}>
                        Export brief
                      </Button>
                    )}
                  </Show>
                  <Show when={campaignCommand("delete")}>
                    {(command) => (
                      <Button type="button" variant="ghost" onClick={() => props.onSubmit(command(), { campaignID: plan.id })}>
                        Delete brief
                      </Button>
                    )}
                  </Show>
                </div>
              </div>
            )}
          </For>
        </div>
      </div>
    </div>
  )
}

function QueueEmptyState() {
  return (
    <div class="rounded-md border border-dashed border-border-weak-base bg-background-base px-4 py-8 text-center">
      <div class="mx-auto mb-2 h-10 w-10 rounded-md bg-background-secondary" />
      <div class="text-13-medium text-text-strong">No generations yet</div>
      <div class="text-12-regular text-text-base">Write a brief above and hit Generate.</div>
    </div>
  )
}

function GalleryEmptyState() {
  return (
    <div class="rounded-md border border-dashed border-border-weak-base bg-background-base px-4 py-8 text-center md:col-span-2">
      <div class="mx-auto mb-2 h-10 w-10 rounded-md bg-background-secondary" />
      <div class="text-13-medium text-text-strong">Completed outputs appear here</div>
      <div class="text-12-regular text-text-base">Save the ones you want to keep.</div>
    </div>
  )
}

function AssetsEmptyState() {
  return (
    <div class="rounded-md border border-dashed border-border-weak-base bg-background-base px-3 py-4 text-center text-12-regular text-text-base">
      No reference assets yet.
    </div>
  )
}

function ErrorBlock(props: { error: unknown }) {
  const humanised = humaniseError(props.error)
  return (
    <div class="mt-2 flex flex-col gap-1">
      <div class="text-12-medium text-text-danger">{humanised.title}</div>
      <details class="text-11-regular text-text-base">
        <summary class="cursor-pointer">Details</summary>
        <pre class="mt-1 whitespace-pre-wrap text-11-regular">{String(props.error)}</pre>
      </details>
    </div>
  )
}

function humaniseError(error: unknown): { title: string } {
  const msg = String(error ?? "")
  if (/401|unauthori[sz]ed|sign in|session/i.test(msg)) return { title: "Your session expired. Sign in again." }
  if (/403|forbidden/i.test(msg)) return { title: "This workspace can't use generation right now. Contact support." }
  if (/disabled|kill.?switch/i.test(msg)) return { title: "Generative Studio is disabled for this workspace." }
  if (/timeout|timed out|network|fetch failed|econnreset|etimedout|expired/i.test(msg)) return { title: "Generation timed out. Try again." }
  if (/\b(?:429|503)\b|high demand|overloaded|quota|rate.?limit|resource exhausted|try again later/i.test(msg)) {
    return { title: "Provider is busy. Try again shortly." }
  }
  if (/content policy|safety|policy violation|blocked by .*policy|prompt blocked|prohibited/i.test(msg)) {
    return { title: "Prompt blocked by content policy. Try rewording." }
  }
  return { title: "Generation failed." }
}

function mimeKind(mime: string): "image" | "audio" | "video" | "unknown" {
  if (mime.startsWith("image/")) return "image"
  if (mime.startsWith("video/")) return "video"
  if (mime.startsWith("audio/")) return "audio"
  return "unknown"
}

function aspectClass(job: Record<string, unknown>) {
  const params = (job.params as Record<string, unknown>) ?? {}
  const ratio = String(params.aspect_ratio ?? "1:1")
  if (ratio === "16:9") return "aspect-[16/9]"
  if (ratio === "9:16") return "aspect-[9/16]"
  if (ratio === "4:3") return "aspect-[4/3]"
  if (ratio === "3:4") return "aspect-[3/4]"
  return "aspect-square"
}

function formatParam(value: unknown): string {
  if (value === null || value === undefined) return ""
  if (typeof value === "object") return JSON.stringify(value)
  return String(value)
}

function formatBytes(value: number) {
  if (value < 1024) return `${value} B`
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`
  return `${(value / 1024 / 1024).toFixed(1)} MB`
}

function formatMs(value: unknown) {
  const ms = Number(value)
  if (!Number.isFinite(ms)) return null
  if (ms < 1000) return `${Math.round(ms)} ms`
  return `${(ms / 1000).toFixed(1)} s`
}

function formatTraceTime(value: unknown) {
  const stamp = Number(value)
  if (!Number.isFinite(stamp)) return ""
  return new Date(stamp).toLocaleTimeString()
}

function traceBadges(trace: Record<string, unknown>) {
  const metadata = typeof trace.metadata === "object" && trace.metadata ? (trace.metadata as Record<string, unknown>) : {}
  return [
    formatMs(metadata.openAnalystSubmitMs) ? `submit API ${formatMs(metadata.openAnalystSubmitMs)}` : null,
    formatMs(metadata.openAnalystPollMs) ? `poll API ${formatMs(metadata.openAnalystPollMs)}` : null,
    formatMs(metadata.backendWaitMs) ? `backend wait ${formatMs(metadata.backendWaitMs)}` : null,
    formatMs(metadata.assetUploadMs) ? `asset upload ${formatMs(metadata.assetUploadMs)}` : null,
    formatMs(metadata.downloadMs) ? `download ${formatMs(metadata.downloadMs)}` : null,
    typeof metadata.downloadBytes === "number" ? `download ${formatBytes(metadata.downloadBytes)}` : null,
    typeof metadata.backendStatus === "string" && metadata.backendStatus ? `backend ${metadata.backendStatus}` : null,
    typeof metadata.downloadHost === "string" && metadata.downloadHost ? metadata.downloadHost : null,
  ].filter((item): item is string => Boolean(item))
}

function outputUrl(job: Record<string, unknown>) {
  const output = job.output as Record<string, unknown> | undefined
  return typeof output?.url === "string" && output.url ? output.url : undefined
}

function outputKind(job: Record<string, unknown>) {
  const url = outputUrl(job) ?? ""
  const output = job.output as Record<string, unknown> | undefined
  if (typeof output?.mime === "string" && output.mime.startsWith("video/")) return "video"
  if (/\.(mp4|mov|webm)(\?|$)/i.test(url)) return "video"
  return ["text-to-video", "image-to-video", "lip-sync", "cinema"].includes(String(job.mode)) ? "video" : "image"
}

function jobVisibleInStudio(job: Record<string, unknown>, studio: string) {
  if (String(job.studio) === studio) return true
  return studio === "image" && String(job.studio) === "workflows" && outputKind(job) === "image"
}
